import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, isSameDay, addDays, differenceInDays, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  ChevronLeft, ChevronRight, ChevronDown, Loader2, X, Calendar, User,
  Building2, Copy, GripHorizontal, Briefcase, Coffee, Heart, Thermometer,
  Banknote, Save, Trash2, AlertTriangle, Ban, Plus, Pencil, Filter, Users, Check, MoreHorizontal,
  ClipboardCheck, MessageSquare, FileEdit, Eye, Search
} from 'lucide-react';
import api from '../services/api';
import { getApiError } from '@/lib/utils';
import RegionFilter from '../components/RegionFilter';
import DuplicateJobConfirmDialog from '../components/DuplicateJobConfirmDialog';
import { LockBadge, LockToggleButton, LockLegend, isLockedError, showLockedToast } from '../components/JobLock';
import { Lock } from 'lucide-react';

import BatchPublishReviewDialog from '../components/BatchPublishReviewDialog';
const EVENT_TYPES = [
  { value: 'CLIENT_JOB', label: 'Client Job', icon: Briefcase, color: 'bg-blue-100 border-blue-300 text-blue-800' },
  { value: 'LEAVE_HOLIDAY', label: 'Holiday', icon: Coffee, color: 'bg-green-100 border-green-300 text-green-800' },
  { value: 'LEAVE_SICK', label: 'Sick Leave', icon: Thermometer, color: 'bg-red-100 border-red-300 text-red-800' },
  { value: 'LEAVE_COMPASSIONATE', label: 'Compassionate', icon: Heart, color: 'bg-purple-100 border-purple-300 text-purple-800' },
  { value: 'LEAVE_OTHER', label: 'Other', icon: MoreHorizontal, color: 'bg-slate-100 border-slate-300 text-slate-800', hasCustomDescription: true }
];

const getEventTypeInfo = (eventType) => {
  return EVENT_TYPES.find(t => t.value === eventType) || EVENT_TYPES[0];
};

// iter451 — Shared, stable empty array sentinel for cell lookups. Returning
// a fresh `[]` from `getJobsForCellFast` on every render would break shallow
// equality in child memoisation and force needless re-renders.
const EMPTY_ARR = [];


/**
 * ApproveDialogDateList — expandable per-date list for the Senior Scheduler
 * review-approve dialog.
 *
 * Reads from /batch-schedule/batch/{id}/jobs (NOT the month-scoped /grid)
 * so Block Bookings whose pairs span two or more months show every date.
 * Fail-closed from the backend: non-reviewers get 403; cross-tenant hits
 * get 403; a deleted or missing batch gets 404.
 *
 * Dates are grouped by (year, month) so a cross-month BB is unmistakable
 * to the approver.  No client-side filtering — if the backend returns a
 * row, it is in this batch.
 */
function ApproveDialogDateList({ batch }) {
  const [loading, setLoading] = React.useState(false);
  const [jobs, setJobs]       = React.useState([]);
  const [error, setError]     = React.useState(null);

  React.useEffect(() => {
    if (!batch?.id) return;
    setLoading(true);
    setError(null);
    setJobs([]);
    api.get(`/batch-schedule/batch/${batch.id}/jobs`)
      .then((resp) => setJobs(resp.data || []))
      .catch((err) => setError(getApiError(err) || 'Failed to load batch dates'))
      .finally(() => setLoading(false));
  }, [batch?.id]);

  if (!batch?.id) return null;
  if (loading) return (
    <p className="text-xs text-muted-foreground" data-testid="approve-dates-loading">Loading dates…</p>
  );
  if (error) return (
    <p className="text-xs text-destructive" data-testid="approve-dates-error">{error}</p>
  );
  if (!jobs.length) return (
    <p className="text-xs text-muted-foreground" data-testid="approve-dates-empty">No dates in this batch.</p>
  );

  // Group by YYYY-MM for cross-month clarity.
  const groups = jobs.reduce((acc, j) => {
    const d = j.scheduled_date ? String(j.scheduled_date).slice(0, 7) : 'unknown';
    (acc[d] = acc[d] || []).push(j);
    return acc;
  }, {});
  const keys = Object.keys(groups).sort();

  return (
    <div className="border rounded-md p-2 max-h-56 overflow-y-auto" data-testid="approve-dates-list">
      <p className="text-xs font-medium mb-2">
        {jobs.length} date{jobs.length !== 1 ? 's' : ''} in this batch
        {keys.length > 1 && ` across ${keys.length} months`}
      </p>
      {keys.map((k) => (
        <div key={k} className="mb-2 last:mb-0">
          <p className="text-xs font-semibold text-muted-foreground mb-1" data-testid={`approve-dates-month-${k}`}>
            {k}
          </p>
          <ul className="text-xs space-y-0.5 ml-2">
            {groups[k].map((j) => (
              <li key={j.id} data-testid={`approve-date-${j.id}`}>
                {j.scheduled_date}
                {j.end_date && j.end_date !== j.scheduled_date ? ` → ${j.end_date}` : ''}
                {j.site_name ? ` · ${j.site_name}` : ''}
                {j.client_reference ? ` · ref ${j.client_reference}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}



// Helper to update grid data locally without refetching
const updateGridDataLocally = (gridData, jobId, updates) => {
  if (!gridData || !gridData.jobs) return gridData;
  
  return {
    ...gridData,
    jobs: gridData.jobs.map(job => 
      job.id === jobId ? { ...job, ...updates } : job
    )
  };
};

// Helper to add a job to grid data locally
const addJobToGridLocally = (gridData, newJob) => {
  if (!gridData) return gridData;
  return {
    ...gridData,
    jobs: [...(gridData.jobs || []), newJob]
  };
};

// Helper to remove a job from grid data locally
const removeJobFromGridLocally = (gridData, jobId) => {
  if (!gridData || !gridData.jobs) return gridData;
  return {
    ...gridData,
    jobs: gridData.jobs.filter(job => job.id !== jobId)
  };
};

export default function BatchEntryPage() {
  const { user, isAdmin, isSeniorScheduler, isScheduler } = useAuth();
  const navigate = useNavigate();
  const { settings, formatCurrency: settingsFormatCurrency, region, currencySymbol } = useSettings();
  const { toast } = useToast();
  
  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [gridData, setGridData] = useState(null);
  const [services, setServices] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  
  // Filter state - NEW
  const [filterAuditorIds, setFilterAuditorIds] = useState([]); // Empty = all, array = specific auditors
  const [filterSiteIds, setFilterSiteIds] = useState([]);       // Empty = all sites
  const [siteSearchQuery, setSiteSearchQuery] = useState('');    // keyboard search inside popover
  // iter472 — Persist Month/Week/Custom preference per user so schedulers
  // who plan around month-end don't reset to Month view every login.
  const _viewPrefKey = user?.id ? `batchEntryDateRange:${user.id}` : 'batchEntryDateRange';
  const [filterDateRange, setFilterDateRangeRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(_viewPrefKey);
      if (saved === 'month' || saved === 'week' || saved === 'custom') return saved;
    } catch (_err) { /* ignore localStorage errors (SSR, quota, etc.) */ }
    return 'month';
  });
  const setFilterDateRange = useCallback((next) => {
    setFilterDateRangeRaw(next);
    try { localStorage.setItem(_viewPrefKey, next); } catch (_err) { /* ignore */ }
  }, [_viewPrefKey]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // iter393 — User-controllable "show auditors with jobs only" toggle.
  // Previously this was hard-coded to true. Default remains true (the
  // matrix collapses dead columns) but users can now flip it to see
  // every active auditor — e.g. when assigning a new job to someone
  // who happens to have no jobs in the visible window.
  const [showAuditorsWithJobsOnly, setShowAuditorsWithJobsOnly] = useState(true);
  const [regionId, setRegionId] = useState(null);
  
  // iter397 — Schedule Lock state (Senior Scheduler / Admin only).
  // `activeLocks` is fetched on mount; the matrix cells show a small
  // lock badge when an auditor×date sits inside any active lock.
  const [activeLocks, setActiveLocks] = useState([]);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockFormStart, setLockFormStart] = useState('');
  const [lockFormEnd, setLockFormEnd] = useState('');
  const [lockFormAuditorIds, setLockFormAuditorIds] = useState([]);
  const [lockFormReason, setLockFormReason] = useState('');
  const [lockSaving, setLockSaving] = useState(false);
  
  // Autocomplete state
  const [activeCell, setActiveCell] = useState(null); // { auditorId, date }
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Side panel state
  const [selectedJob, setSelectedJob] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // "Other" leave dialog state
  const [otherLeaveDialogOpen, setOtherLeaveDialogOpen] = useState(false);
  const [otherLeaveDescription, setOtherLeaveDescription] = useState('');
  
  // Pricing lines state for side panel
  const [jobPricingLines, setJobPricingLines] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricingLine, setEditingPricingLine] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    service_id: '',
    price: '',
    currency: region === 'UAE' ? 'AED' : 'GBP',
    description: ''
  });
  
  // Drag state for extending/shrinking jobs
  const [dragState, setDragState] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [copiedJob, setCopiedJob] = useState(null);
  const [copying, setCopying] = useState(false); // Prevent double paste
  
  // Selected cell for keyboard shortcuts
  const [selectedCell, setSelectedCell] = useState(null);
  
  // Dropdown position state for portal rendering
  const [dropdownPosition, setDropdownPosition] = useState(null);
  
  // Batch workflow state (Phase 2A)
  const [draftBatches, setDraftBatches] = useState([]);
  const [returnedBatches, setReturnedBatches] = useState([]); // Returned batches for rework
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [publishingBatch, setPublishingBatch] = useState(false);
  // iter468 — Approved-but-not-yet-published batches (Senior Scheduler workflow)
  const [unpublishedBatches, setUnpublishedBatches] = useState([]);
  
  // Reviewer state (Senior Scheduler / Admin)
  const [pendingBatches, setPendingBatches] = useState([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBatchForReview, setSelectedBatchForReview] = useState(null);
  const [reviewAction, setReviewAction] = useState(null); // 'approve', 'return', 'delete'
  const [reviewComments, setReviewComments] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // iter431 — duplicate-job confirm dialog state. When the server
  // returns 409 DUPLICATE_JOB_EXISTS we hold the pending job payload
  // here and re-submit with confirm_duplicate=true on confirmation.
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  // { detail: serverDetail, retry: () => Promise<void> }
  
  // Batch drill-down state (Phase 2A requirement: show jobs in selected batch)
  const [batchDrilldownOpen, setBatchDrilldownOpen] = useState(false);
  const [selectedBatchForDrilldown, setSelectedBatchForDrilldown] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]);
  const [batchProposals, setBatchProposals] = useState([]);  // Change proposals in batch
  const [batchDrilldownAuditors, setBatchDrilldownAuditors] = useState([]);  // Auditors for the batch's month
  const [loadingBatchJobs, setLoadingBatchJobs] = useState(false);
  
  // Returned batch details dialog
  const [returnedBatchDialogOpen, setReturnedBatchDialogOpen] = useState(false);
  const [selectedReturnedBatch, setSelectedReturnedBatch] = useState(null);
  
  // Refs
  const searchInputRef = useRef(null);
  const gridContainerRef = useRef(null);
  
  // Get month/year from currentDate
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  
  // Calculate days based on filter
  const daysToShow = useMemo(() => {
    if (filterDateRange === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
      const end = endOfWeek(currentDate, { weekStartsOn: 1 }); // Sunday
      return eachDayOfInterval({ start, end });
    } else if (filterDateRange === 'custom' && customStartDate && customEndDate) {
      try {
        const start = parseISO(customStartDate);
        const end = parseISO(customEndDate);
        if (start <= end) {
          return eachDayOfInterval({ start, end });
        }
      } catch (e) {
        // Fall through to month view
      }
    }
    // Default: full month
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate, filterDateRange, customStartDate, customEndDate]);
  
  // Unique sites present in the current grid window — used by the site filter.
  // Sorted alphabetically by site name so the list is predictable.
  const availableSites = useMemo(() => {
    const jobs = gridData?.jobs || [];
    const siteMap = new Map();
    for (const job of jobs) {
      if (job.site_id && job.site_name && !siteMap.has(job.site_id)) {
        siteMap.set(job.site_id, {
          id: job.site_id,
          name: job.site_name,
          client_name: job.client_name || '',
        });
      }
    }
    return Array.from(siteMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [gridData?.jobs]);

  // Sites list after applying the keyboard search query — what the dropdown renders.
  const filteredSiteOptions = useMemo(() => {
    const q = siteSearchQuery.trim().toLowerCase();
    if (!q) return availableSites;
    return availableSites.filter(s =>
      s.name.toLowerCase().includes(q) || s.client_name.toLowerCase().includes(q)
    );
  }, [availableSites, siteSearchQuery]);

  // Filter auditors based on selection (multi-select)
  // iter363c / iter393 — When `showAuditorsWithJobsOnly` is true (the
  // default), hide auditors who have zero jobs in the currently visible
  // date range so the matrix doesn't waste horizontal space on empty
  // columns (e.g. an auditor on leave for the whole month). When the
  // user flips the toggle off, show every active auditor regardless of
  // whether they have a job in the window — useful when assigning a
  // brand-new job to someone with no current activity.
  const filteredAuditors = useMemo(() => {
    const auditors = gridData?.auditors || [];
    const pool = filterAuditorIds.length === 0
      ? auditors
      : auditors.filter(a => filterAuditorIds.includes(a.id));
    // Explicit auditor selection always wins — never auto-hide.
    if (filterAuditorIds.length > 0) return pool;
    // User chose to see everyone — skip the with-jobs collapse.
    if (!showAuditorsWithJobsOnly) return pool;
    if (!gridData?.jobs || daysToShow.length === 0) return pool;
    const dateStrs = daysToShow.map(d => format(d, 'yyyy-MM-dd'));
    const dateSet = new Set(dateStrs);
    const minDate = dateStrs[0];
    const maxDate = dateStrs[dateStrs.length - 1];
    const auditorsWithJobs = new Set();
    // Inline `auditorWorksDate` check (declared later as a useCallback)
    // so we avoid a TDZ reference here.
    const worksOn = (job, aid, ds) => {
      if (!job.auditor_ids?.includes(aid)) return false;
      if (job.assignments && job.assignments.length > 0) {
        const personal = job.assignments.filter(a => a.auditor_id === aid);
        if (personal.length > 0) {
          return personal.some(a => ds >= a.start_date && ds <= a.end_date);
        }
      }
      if (job.job_dates && job.job_dates.length > 0) {
        return job.job_dates.includes(ds);
      }
      const jobStart = job.scheduled_date;
      const jobEnd = job.end_date || job.scheduled_date;
      return ds >= jobStart && ds <= jobEnd;
    };
    for (const job of gridData.jobs) {
      // Cheap range overlap filter first
      const jobStart = job.scheduled_date;
      const jobEnd = job.end_date || job.scheduled_date;
      if (jobEnd < minDate || jobStart > maxDate) continue;
      const auditorIds = job.auditor_ids || [];
      for (const aid of auditorIds) {
        if (auditorsWithJobs.has(aid)) continue;
        for (const ds of dateStrs) {
          if (dateSet.has(ds) && worksOn(job, aid, ds)) {
            auditorsWithJobs.add(aid);
            break;
          }
        }
      }
    }
    const base = pool.filter(a => auditorsWithJobs.has(a.id));
    // Site filter: when active, further restrict to auditors who have at least
    // one job for one of the selected sites in the visible date window.
    if (filterSiteIds.length === 0) return base;
    const siteSet = new Set(filterSiteIds);
    return base.filter(a =>
      (gridData?.jobs || []).some(job =>
        job.site_id && siteSet.has(job.site_id) && job.auditor_ids?.includes(a.id)
      )
    );
  }, [gridData, filterAuditorIds, filterSiteIds, daysToShow, showAuditorsWithJobsOnly]);
  
  // Load grid data
  const loadGridData = useCallback(async () => {
    setLoading(true);
    try {
      // iter472 — When Week View spans two months (e.g. Feb 27 – Mar 5),
      // fetch BOTH months' grid data so jobs that live in the "other"
      // month still appear in the matrix. Auditors + jobs are merged and
      // deduped by id; the primary month's shape (days_in_month,
      // first_weekday, month, year) is preserved so the rest of the
      // matrix keeps working exactly as it did in Month view.
      const isWeek = filterDateRange === 'week';
      const spansAnotherMonth =
        isWeek && daysToShow.length > 0 && (
          daysToShow[0].getMonth() !== daysToShow[daysToShow.length - 1].getMonth() ||
          daysToShow[0].getFullYear() !== daysToShow[daysToShow.length - 1].getFullYear()
        );

      const primaryParams = { month, year, region: region || undefined, region_id: regionId != null ? regionId : undefined };
      if (!spansAnotherMonth) {
        const response = await api.get('/batch-schedule/grid', { params: primaryParams });
        setGridData(response.data);
        return;
      }

      const firstDay = daysToShow[0];
      const lastDay = daysToShow[daysToShow.length - 1];
      const secondMonth = (firstDay.getMonth() + 1 !== month || firstDay.getFullYear() !== year)
        ? { month: firstDay.getMonth() + 1, year: firstDay.getFullYear() }
        : { month: lastDay.getMonth() + 1, year: lastDay.getFullYear() };

      const [primaryRes, extraRes] = await Promise.all([
        api.get('/batch-schedule/grid', { params: primaryParams }),
        api.get('/batch-schedule/grid', {
          params: { ...primaryParams, month: secondMonth.month, year: secondMonth.year },
        }),
      ]);

      const primary = primaryRes.data || {};
      const extra = extraRes.data || {};
      const mergedAuditorsById = new Map();
      [...(primary.auditors || []), ...(extra.auditors || [])].forEach(a => {
        if (a && a.id != null && !mergedAuditorsById.has(a.id)) mergedAuditorsById.set(a.id, a);
      });
      const mergedJobsById = new Map();
      [...(primary.jobs || []), ...(extra.jobs || [])].forEach(j => {
        if (j && j.id != null && !mergedJobsById.has(j.id)) mergedJobsById.set(j.id, j);
      });

      setGridData({
        ...primary,
        auditors: Array.from(mergedAuditorsById.values()),
        jobs: Array.from(mergedJobsById.values()),
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load schedule data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [month, year, region, regionId, toast, filterDateRange, daysToShow]);
  
  // Load services
  const loadServices = useCallback(async () => {
    try {
      const response = await api.get('/batch-schedule/services');
      setServices(response.data);
    } catch (err) {
      console.error('Failed to load services:', err);
    }
  }, []);

  // iter397 — Load active schedule locks. Visible to all internal
  // users so schedulers/auditors see the lock badges; only Senior
  // Schedulers + Admins can mutate them.
  const loadLocks = useCallback(async () => {
    try {
      const response = await api.get('/batch-schedule/locks', { params: { active_only: true } });
      setActiveLocks(response.data || []);
    } catch (err) {
      console.error('Failed to load schedule locks:', err);
    }
  }, []);

  // iter397 — Helper: is the given (auditor_id, date) inside any active lock?
  const isCellLocked = useCallback((auditorId, dateStr) => {
    if (!activeLocks || activeLocks.length === 0) return null;
    for (const lock of activeLocks) {
      if (dateStr < lock.start_date || dateStr > lock.end_date) continue;
      const auds = lock.auditor_ids || [];
      if (auds.length === 0 || auds.includes(auditorId)) return lock;
    }
    return null;
  }, [activeLocks]);

  // iter397 — Open the lock dialog. Default date range = the visible
  // batch window; default auditor set = ALL auditors pre-ticked (user
  // can deselect any they don't want included).
  const openLockDialog = () => {
    if (daysToShow.length === 0) return;
    setLockFormStart(format(daysToShow[0], 'yyyy-MM-dd'));
    setLockFormEnd(format(daysToShow[daysToShow.length - 1], 'yyyy-MM-dd'));
    const allIds = (gridData?.auditors || []).map(a => a.id);
    setLockFormAuditorIds(allIds);
    setLockFormReason('');
    setLockDialogOpen(true);
  };

  const submitLock = async () => {
    setLockSaving(true);
    try {
      const allIds = (gridData?.auditors || []).map(a => a.id);
      // If the user kept every auditor ticked, persist the ALL sentinel
      // ([]) — keeps the DB row semantic-clean and future-proof against
      // auditors being added later.
      const auditorPayload = (
        lockFormAuditorIds.length > 0 &&
        lockFormAuditorIds.length === allIds.length
      ) ? [] : lockFormAuditorIds;
      await api.post('/batch-schedule/locks', {
        start_date: lockFormStart,
        end_date: lockFormEnd,
        auditor_ids: auditorPayload,
        reason: lockFormReason || null,
      });
      toast({ title: 'Schedule locked', description: 'Lock created — Senior/Admins will receive email on any future change in the locked window.' });
      setLockDialogOpen(false);
      await loadLocks();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create lock';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLockSaving(false);
    }
  };

  const unlockOne = async (lockId) => {
    // iter397 polish — optimistic removal so the dialog list and the
    // "N active locks" badge update immediately, then reconcile against
    // the server response.
    setActiveLocks(prev => prev.filter(l => l.id !== lockId));
    try {
      await api.delete(`/batch-schedule/locks/${lockId}`);
      toast({ title: 'Unlocked', description: `Schedule lock #${lockId} deactivated.` });
      await loadLocks();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to unlock';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      // Reconcile on failure so the optimistic removal doesn't stick.
      await loadLocks();
    }
  };

  // Load bank holidays
  const loadBankHolidays = useCallback(async () => {
    try {
      const response = await api.get('/holidays', {
        params: { year, include_inactive: false, region: region || undefined }
      });
      setBankHolidays(response.data || []);
    } catch (err) {
      console.error('Failed to load bank holidays:', err);
      setBankHolidays([]);
    }
  }, [year, region]);
  
  // iter467/scope-update — Admin now follows the same draft workflow as Senior
  // Scheduler (bypasses_draft_workflow returns false on backend). isPureAdmin is
  // retained as a role flag (true for admin without senior_scheduler) but no
  // longer implies "bypasses drafts".
  const isPureAdmin = isAdmin && !isSeniorScheduler;

  // Combined Approve & Publish dialog (replaces separate Review + Publish buttons)
  const [apDialogOpen, setApDialogOpen] = useState(false);
  // Job-level count for button badge (loaded async; null = loading)
  const [pendingJobCount, setPendingJobCount] = useState(null);

  // Load draft batches for the current user (Phase 2A + scope-update)
  // Admin now goes through the draft workflow (same as Senior Scheduler),
  // so we load drafts for all authenticated scheduler/admin roles.
  const loadDraftBatches = useCallback(async () => {
    if (!(isScheduler || isSeniorScheduler || isAdmin)) {
      setDraftBatches([]);
      setReturnedBatches([]);
      setUnpublishedBatches([]);
      return;
    }
    try {
      const mine = !isSeniorScheduler && !isAdmin; // SS/Admin see all
      const requests = [
        api.get('/batch-schedule/batches', { params: { status: 'draft', mine } }),
        api.get('/batch-schedule/batches', { params: { status: 'returned', mine } }),
      ];
      if (isSeniorScheduler || isAdmin) {
        requests.push(api.get('/batch-schedule/batches', { params: { status: 'approved' } }));
      }
      const [draftResponse, returnedResponse, approvedResponse] = await Promise.all(requests);
      setDraftBatches((draftResponse.data || []).filter(b => b.job_count > 0 || b.proposal_count > 0));
      setReturnedBatches((returnedResponse.data || []).filter(b => b.job_count > 0 || b.proposal_count > 0));
      if ((isSeniorScheduler || isAdmin) && approvedResponse) {
        setUnpublishedBatches(
          (approvedResponse.data || []).filter(
            b => (b.published_at === null || b.published_at === undefined) && (b.job_count > 0 || b.proposal_count > 0)
          )
        );
      } else {
        setUnpublishedBatches([]);
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
      setDraftBatches([]);
      setReturnedBatches([]);
      setUnpublishedBatches([]);
    }
  }, [isScheduler, isSeniorScheduler, isAdmin]);
  
  // Submit batch for approval (Phase 2A)
  const handleSubmitBatch = async (batchId) => {
    setSubmittingBatch(true);
    try {
      const response = await api.post(`/batch-schedule/batch/${batchId}/submit`);
      toast({
        title: 'Batch Submitted',
        description: response.data.message || 'Your batch has been submitted for approval'
      });
      // Refresh batches and grid data
      await loadDraftBatches();
      await loadGridData();
      // Close returned batch dialog if open
      setReturnedBatchDialogOpen(false);
      setSelectedReturnedBatch(null);
    } catch (err) {
      toast({
        title: 'Submission Failed',
        description: getApiError(err, 'Failed to submit batch for approval'),
        variant: 'destructive'
      });
    } finally {
      setSubmittingBatch(false);
    }
  };

  // iter467 — One-click Publish (submit + approve) for Senior Schedulers.
  const handlePublishBatch = async (batchId) => {
    setPublishingBatch(true);
    try {
      const response = await api.post(`/batch-schedule/batch/${batchId}/publish`);
      toast({
        title: 'Batch Published',
        description: response.data.message || 'Your batch has been published — jobs are now visible to everyone.'
      });
      await loadDraftBatches();
      await loadGridData();
      setReturnedBatchDialogOpen(false);
      setSelectedReturnedBatch(null);
    } catch (err) {
      toast({
        title: 'Publish Failed',
        description: getApiError(err, 'Failed to publish batch'),
        variant: 'destructive'
      });
    } finally {
      setPublishingBatch(false);
    }
  };
  
  // Cancel draft batch - deletes draft and associated jobs/proposals
  const [cancellingBatch, setCancellingBatch] = useState(false);
  const handleCancelDraft = async (batchId, batchName) => {
    if (!window.confirm(`Are you sure you want to cancel "${batchName || `Batch #${batchId}`}"?\n\nThis will permanently delete all jobs and proposals in this draft. This action cannot be undone.`)) {
      return;
    }
    setCancellingBatch(true);
    try {
      const response = await api.post(`/batch-schedule/batch/${batchId}/cancel-draft`);
      toast({
        title: 'Draft Cancelled',
        description: response.data.message || 'Draft has been cancelled and all associated jobs deleted'
      });
      await loadDraftBatches();
      await loadGridData();
      setReturnedBatchDialogOpen(false);
      setSelectedReturnedBatch(null);
    } catch (err) {
      toast({
        title: 'Cancel Failed',
        description: getApiError(err, 'Failed to cancel draft'),
        variant: 'destructive'
      });
    } finally {
      setCancellingBatch(false);
    }
  };
  
  // Preview notification email before submitting
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBatchId, setPreviewBatchId] = useState(null);
  const [previewBatchName, setPreviewBatchName] = useState('');
  const handlePreviewNotification = async (batchId, batchName) => {
    setPreviewLoading(true);
    setPreviewBatchId(batchId);
    setPreviewBatchName(batchName || `Batch #${batchId}`);
    try {
      const response = await api.get(`/batch-schedule/batch/${batchId}/preview-notification`);
      setPreviewHtml(response.data.email_html || '<p>No preview available</p>');
      setPreviewOpen(true);
    } catch (err) {
      toast({
        title: 'Preview Failed',
        description: getApiError(err, 'Failed to load notification preview'),
        variant: 'destructive'
      });
    } finally {
      setPreviewLoading(false);
    }
  };
  const handlePreviewAndSubmit = async () => {
    setPreviewOpen(false);
    if (previewBatchId) {
      await handleSubmitBatch(previewBatchId);
    }
  };
  
  // Open returned batch details dialog
  const openReturnedBatchDialog = (batch) => {
    setSelectedReturnedBatch(batch);
    setReturnedBatchDialogOpen(true);
  };
  
  // Load pending batches for review (Senior Scheduler / Admin)
  const loadPendingBatches = useCallback(async () => {
    if (!isSeniorScheduler && !isAdmin) {
      setPendingBatches([]);
      return;
    }
    try {
      // FIXED: Load ALL pending_approval batches (any month/year) - so Review button always shows
      const response = await api.get('/batch-schedule/batches', {
        params: { status: 'pending_approval' }
      });
      setPendingBatches(response.data || []);
    } catch (err) {
      console.error('Failed to load pending batches:', err);
      setPendingBatches([]);
    }
  }, [isSeniorScheduler, isAdmin]);
  
  // Handle batch review actions (approve, return, delete)
  const handleReviewAction = async () => {
    if (!selectedBatchForReview || !reviewAction) return;
    
    // Validate comments for return/delete
    if ((reviewAction === 'return' || reviewAction === 'delete') && !reviewComments.trim()) {
      toast({
        title: 'Comments Required',
        description: `Please provide a reason for ${reviewAction === 'return' ? 'returning' : 'deleting'} this batch`,
        variant: 'destructive'
      });
      return;
    }
    
    setReviewLoading(true);
    try {
      const endpoint = `/batch-schedule/batch/${selectedBatchForReview.id}/${reviewAction}`;
      const payload = reviewAction === 'approve' ? {} : { comments: reviewComments.trim() };
      const response = await api.post(endpoint, payload);
      
      toast({
        title: reviewAction === 'approve' ? 'Batch Approved' : 
               reviewAction === 'return' ? 'Batch Returned' : 'Batch Deleted',
        description: response.data.message
      });
      
      // Reset and refresh
      setReviewDialogOpen(false);
      setSelectedBatchForReview(null);
      setReviewAction(null);
      setReviewComments('');
      await loadPendingBatches();
      await loadGridData();
    } catch (err) {
      toast({
        title: 'Action Failed',
        description: err.response?.data?.detail || `Failed to ${reviewAction} batch`,
        variant: 'destructive'
      });
    } finally {
      setReviewLoading(false);
    }
  };
  
  // Open review dialog
  const openReviewDialog = (batch, action) => {
    setSelectedBatchForReview(batch);
    setReviewAction(action);
    setReviewComments('');
    setReviewDialogOpen(true);
  };
  
  // Open batch drilldown dialog to show jobs in a batch (Phase 2A requirement).
  //
  // Uses /batch/{id}/jobs so EVERY date in the batch is listed, including
  // Block Booking pairs scheduled in a different month from the batch's
  // nominal month/year.  A plain /grid read scoped to batch.month/year
  // would silently omit those cross-month pairs — do not reintroduce the
  // client-side filter approach for this surface.
  const openBatchDrilldown = async (batch) => {
    setSelectedBatchForDrilldown(batch);
    setBatchDrilldownOpen(true);
    setLoadingBatchJobs(true);
    setBatchJobs([]);
    setBatchProposals([]);  // Reset proposals
    setBatchDrilldownAuditors([]);  // Reset auditors

    try {
      const [batchJobsResponse, proposalsResponse] = await Promise.all([
        api.get(`/batch-schedule/batch/${batch.id}/jobs`),
        // Also fetch any change proposals for this batch
        batch.proposal_count > 0
          ? api.get(`/batch-schedule/batch/${batch.id}/proposals`)
          : Promise.resolve({ data: [] }),
      ]);

      setBatchJobs(batchJobsResponse.data || []);
      setBatchProposals(proposalsResponse.data || []);
      // Auditor name lookup is populated on-demand elsewhere; this list
      // is intentionally minimal — the reviewer sees every date, not every
      // auditor assignment.
      setBatchDrilldownAuditors([]);
    } catch (err) {
      console.error('Failed to load batch jobs:', err);
      toast({
        title: 'Error',
        description: 'Failed to load jobs for this batch',
        variant: 'destructive'
      });
    } finally {
      setLoadingBatchJobs(false);
    }
  };
  
  // Load job-level count for Approve & Publish badge (SS/Admin only)
  const loadPendingJobCount = useCallback(() => {
    if (!(isSeniorScheduler || isAdmin)) return;
    api.get('/batch-schedule/jobs/pending-action')
      .then(r => setPendingJobCount((r.data || []).length))
      .catch(() => setPendingJobCount(null));
  }, [isSeniorScheduler, isAdmin]);

  useEffect(() => {
    loadGridData();
    loadServices();
    loadBankHolidays();
    loadDraftBatches();
    loadPendingBatches();
    loadLocks();
    loadPendingJobCount();
  }, [loadGridData, loadServices, loadBankHolidays, loadDraftBatches, loadPendingBatches, loadLocks, loadPendingJobCount]);
  
  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // iter360 — Suppress batch keyboard shortcuts when typing in any editable
      // field (input/textarea/select/contenteditable). Previously, pressing
      // Backspace inside the "Edit Job Pricing" dialog's price input was
      // triggering handleDeleteJob and wiping the job. The shortcut handler
      // is bound to window so it fires regardless of focus.
      const t = e.target;
      const tag = (t && t.tagName) || '';
      const isEditable = (
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (t && t.isContentEditable) ||
        (t && t.closest && t.closest('[contenteditable="true"]'))
      );
      if (isEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedJob) {
          e.preventDefault();
          setCopiedJob(selectedJob);
          toast({ title: 'Copied', description: 'Press Ctrl+V on a cell to extend this job' });
        }
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (copiedJob && selectedCell) {
          e.preventDefault();
          handlePasteJob(selectedCell.auditorId, selectedCell.date);
        }
      }
      
      if (e.key === 'Escape') {
        setCopiedJob(null);
        setSelectedJob(null);
        setActiveCell(null);
        setSelectedCell(null);
      }
      
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedJob && !activeCell) {
        e.preventDefault();
        handleDeleteJob();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedJob, copiedJob, selectedCell, toast]);

  // iter459 — Batch grid keyboard scrolling.
  //
  // Cross-browser fix: on Safari (macOS) a `<div tabIndex="0">` doesn't
  // reliably retain keyboard focus after clicking on a descendant `<td>`,
  // so the previous per-container `onKeyDown` handler never fired and
  // arrow keys ended up scrolling the whole page. We solve that by
  // attaching a single `document`-level keydown listener that:
  //   1. bails out if focus is on any editable field
  //   2. bails out if the user is holding modifiers we don't own
  //   3. bails out if any modal/dialog is open (Radix uses [data-state=open])
  //   4. otherwise scrolls the batch grid container programmatically
  useEffect(() => {
    const handleScrollKey = (e) => {
      const el = gridContainerRef.current;
      if (!el) return;

      const t = e.target;
      const tag = (t && t.tagName) || '';
      const isEditable = (
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (t && t.isContentEditable) ||
        (t && t.closest && t.closest('[contenteditable="true"]'))
      );
      if (isEditable) return;

      // If a Radix modal / popover is open, let it own the keys.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      // If Ctrl/Meta+C/V/Z/etc. — leave those for other handlers.
      if ((e.ctrlKey || e.metaKey) && !['Home', 'End'].includes(e.key)) return;

      const rowStep = 48;   // one row (~40–48px)
      const colStep = 128;  // one auditor column
      const pageStep = el.clientHeight - rowStep;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          el.scrollBy({ top: rowStep, behavior: 'auto' });
          break;
        case 'ArrowUp':
          e.preventDefault();
          el.scrollBy({ top: -rowStep, behavior: 'auto' });
          break;
        case 'ArrowRight':
          e.preventDefault();
          el.scrollBy({ left: colStep, behavior: 'auto' });
          break;
        case 'ArrowLeft':
          e.preventDefault();
          el.scrollBy({ left: -colStep, behavior: 'auto' });
          break;
        case 'PageDown':
          e.preventDefault();
          el.scrollBy({ top: pageStep, behavior: 'auto' });
          break;
        case 'PageUp':
          e.preventDefault();
          el.scrollBy({ top: -pageStep, behavior: 'auto' });
          break;
        case 'Home':
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          } else {
            el.scrollTo({ top: 0, behavior: 'auto' });
          }
          break;
        case 'End':
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            el.scrollTo({ top: el.scrollHeight, left: el.scrollWidth, behavior: 'auto' });
          } else {
            el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
          }
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleScrollKey);
    return () => document.removeEventListener('keydown', handleScrollKey);
  }, []);


  
  // Search sites
  const searchSites = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await api.get('/batch-schedule/sites/search', {
        params: { q: query }
      });
      setSearchResults(response.data);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeCell) {
        searchSites(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, activeCell, searchSites]);

  // Calculate dropdown position when active cell changes or search input is available
  useEffect(() => {
    if (activeCell && searchInputRef.current) {
      const updatePosition = () => {
        const inputRect = searchInputRef.current?.getBoundingClientRect();
        if (inputRect) {
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - inputRect.bottom;
          const spaceAbove = inputRect.top;
          const dropdownHeight = 200; // Max height of dropdown
          
          // If not enough space below but enough above, position above
          const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
          
          setDropdownPosition({
            left: inputRect.left,
            width: inputRect.width,
            top: shouldShowAbove ? inputRect.top - dropdownHeight - 4 : inputRect.bottom + 4,
            showAbove: shouldShowAbove
          });
        }
      };
      
      // Initial position calculation
      const timer = setTimeout(updatePosition, 50);
      
      // Update on scroll
      const container = gridContainerRef.current;
      if (container) {
        container.addEventListener('scroll', updatePosition);
      }
      window.addEventListener('resize', updatePosition);
      
      return () => {
        clearTimeout(timer);
        if (container) {
          container.removeEventListener('scroll', updatePosition);
        }
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [activeCell]);

  // Check if a date is a bank holiday
  const getBankHoliday = useCallback((dateStr) => {
    return bankHolidays.find(h => h.date === dateStr);
  }, [bankHolidays]);
  
  // Get jobs for a specific cell (auditor + date)
  // iter357 — per-auditor fallback. When job.assignments has rows for SOME
  // auditors but not this one, the auditor still belongs to the job (via
  // auditor_ids — the canonical M2M) and should default to the job's full
  // date span. Without this, auditors with no personal assignment row
  // disappear from the Batch Matrix even though Schedule + the canonical
  // M2M still list them. (Same fix applied server-side in Schedule Matrix
  // PDF and Revenue Forecast / Allocation reports.)
  const auditorWorksDate = useCallback((job, auditorId, dateStr) => {
    if (!job.auditor_ids?.includes(auditorId)) return false;
    if (job.assignments && job.assignments.length > 0) {
      const personal = job.assignments.filter(a => a.auditor_id === auditorId);
      if (personal.length > 0) {
        return personal.some(a => dateStr >= a.start_date && dateStr <= a.end_date);
      }
      // Fall through — no personal assignment → full job span default.
    }
    if (job.job_dates && job.job_dates.length > 0) {
      return job.job_dates.includes(dateStr);
    }
    const jobStart = job.scheduled_date;
    const jobEnd = job.end_date || job.scheduled_date;
    return dateStr >= jobStart && dateStr <= jobEnd;
  }, []);

  const getJobsForCell = useCallback((auditorId, date) => {
    if (!gridData?.jobs) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return gridData.jobs.filter(job => auditorWorksDate(job, auditorId, dateStr));
  }, [gridData, auditorWorksDate]);

  // iter451 — Precompute the (auditorId, dateStr) → jobs lookup for the
  // currently rendered grid so cell rendering is O(1) instead of scanning
  // gridData.jobs on every cell. This eliminates the per-scroll re-layout
  // cost when the grid renders under high job counts and — more importantly
  // — keeps the JS thread free so the compositor can paint smoothly during
  // scroll. Keyed by `${auditorId}|${dateStr}`.
  const cellJobsIndex = useMemo(() => {
    const idx = new Map();
    if (!gridData?.jobs || daysToShow.length === 0) return idx;
    const dateStrs = daysToShow.map(d => format(d, 'yyyy-MM-dd'));
    const auditorIds = (gridData.auditors || []).map(a => a.id);
    const auditorSet = new Set(auditorIds);
    for (const job of gridData.jobs) {
      const ids = job.auditor_ids || [];
      for (const aid of ids) {
        if (!auditorSet.has(aid)) continue;
        for (const ds of dateStrs) {
          if (auditorWorksDate(job, aid, ds)) {
            const k = `${aid}|${ds}`;
            const arr = idx.get(k);
            if (arr) arr.push(job); else idx.set(k, [job]);
          }
        }
      }
    }
    return idx;
  }, [gridData, daysToShow, auditorWorksDate]);

  const getJobsForCellFast = useCallback((auditorId, dateStr) => {
    return cellJobsIndex.get(`${auditorId}|${dateStr}`) || EMPTY_ARR;
  }, [cellJobsIndex]);

  // Check if this is the start date for a job FOR A SPECIFIC AUDITOR
  const isJobStartDate = useCallback((job, dateStr, auditorId) => {
    if (job.assignments && job.assignments.length > 0) {
      // Check ALL assignments for this auditor (supports multiple non-consecutive ranges)
      const auditorAssignments = job.assignments.filter(a => a.auditor_id === auditorId);
      if (auditorAssignments.length > 0) {
        // It's a start date if it matches the start_date of ANY of the auditor's assignments
        return auditorAssignments.some(a => a.start_date === dateStr);
      }
      // iter357 — fall through: auditor on job but no personal row → use job start.
    }

    if (job.job_dates && job.job_dates.length > 0) {
      const sortedDates = [...job.job_dates].sort();
      return sortedDates[0] === dateStr;
    }
    return job.scheduled_date === dateStr;
  }, []);

  // Get job duration in days FOR A SPECIFIC AUDITOR (for a specific date block)
  const getJobDurationForAuditor = useCallback((job, auditorId, forDate = null) => {
    if (job.assignments && job.assignments.length > 0) {
      const auditorAssignments = job.assignments.filter(a => a.auditor_id === auditorId);
      if (auditorAssignments.length > 0) {
        // If forDate provided, find the specific assignment block containing that date
        if (forDate) {
          const dateStr = typeof forDate === 'string' ? forDate : format(forDate, 'yyyy-MM-dd');
          const matchingAssignment = auditorAssignments.find(a => 
            dateStr >= a.start_date && dateStr <= a.end_date
          );
          if (matchingAssignment) {
            const start = parseISO(matchingAssignment.start_date);
            const end = parseISO(matchingAssignment.end_date);
            return differenceInDays(end, start) + 1;
          }
        }
        // Otherwise return total days across all assignments for this auditor
        return auditorAssignments.reduce((total, a) => {
          const start = parseISO(a.start_date);
          const end = parseISO(a.end_date);
          return total + differenceInDays(end, start) + 1;
        }, 0);
      }
      // iter357 — auditor on job but no personal row → use full job span below.
    }
    
    if (job.job_dates && job.job_dates.length > 0) {
      return job.job_dates.length;
    }
    if (!job.end_date || job.end_date === job.scheduled_date) return 1;
    const start = parseISO(job.scheduled_date);
    const end = parseISO(job.end_date);
    return differenceInDays(end, start) + 1;
  }, []);
  
  // Simple duration calculation for display purposes
  const getJobDuration = useCallback((job) => {
    if (job.job_dates && job.job_dates.length > 0) {
      return job.job_dates.length;
    }
    if (!job.end_date || job.end_date === job.scheduled_date) return 1;
    const start = parseISO(job.scheduled_date);
    const end = parseISO(job.end_date);
    return differenceInDays(end, start) + 1;
  }, []);
  
  // Handle cell click
  const handleCellClick = (auditorId, date, e) => {
    setSelectedCell({ auditorId, date });
    
    if (e.target.closest('.job-block')) return;
    
    const existingJobs = getJobsForCell(auditorId, date);
    if (existingJobs.length > 0) return;
    
    setActiveCell({ auditorId, date });
    setSearchQuery('');
    setSearchResults([]);
    
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };
  
  // Handle site selection
  const handleSiteSelect = async (site) => {
    if (!activeCell) return;
    
    // Determine the best default service for this site:
    // 1. Use the site's default pricing service if available
    // 2. Fall back to first service in list
    let defaultServiceId = services[0]?.id;
    try {
      const pricingRes = await api.get(`/sites/${site.id}/pricing`);
      const siteDefaultPricing = pricingRes.data?.find(p => p.is_default);
      if (siteDefaultPricing?.service_id) {
        defaultServiceId = siteDefaultPricing.service_id;
      }
    } catch (err) {
      // Pricing lookup failed — use fallback
    }

    // iter431 — submit helper used both on first try and on
    // "Add anyway" retry after the duplicate confirm dialog.
    const submitCreate = async (confirmDuplicate) => {
      const payload = {
        site_id: site.id,
        service_id: defaultServiceId,
        auditor_ids: [activeCell.auditorId],
        scheduled_date: format(activeCell.date, 'yyyy-MM-dd'),
        event_type: 'CLIENT_JOB',
      };
      if (confirmDuplicate) payload.confirm_duplicate = true;
      return api.post('/batch-schedule/job', payload);
    };

    try {
      const response = await submitCreate(false);

      toast({ title: 'Success', description: 'Job created' });

      // Optimistically add the new job to gridData instead of full reload
      if (gridData && response.data) {
        setGridData(prev => ({
          ...prev,
          jobs: [...(prev.jobs || []), response.data]
        }));
      }
      
      // Refresh batch counts for real-time Submit button update (Phase 2A)
      loadDraftBatches();
      
      setSelectedJob(response.data);
      setEditForm({
        site_id: response.data.site_id,
        service_id: response.data.service_id,
        event_type: response.data.event_type,
        notes: response.data.notes || '',
        client_reference: response.data.client_reference || ''
      });
      
      if (response.data.event_type === 'CLIENT_JOB') {
        try {
          await loadJobPricingLines(response.data.id);
        } catch (pricingErr) {
          console.warn('Could not load pricing lines:', pricingErr);
        }
      }
    } catch (err) {
      // iter431 — duplicate-job 409: hand off to confirmation dialog.
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 409 &&
        detail &&
        typeof detail === 'object' &&
        detail.code === 'DUPLICATE_JOB_EXISTS'
      ) {
        setDuplicateConfirm({
          detail,
          retry: async () => {
            try {
              const response = await submitCreate(true);
              toast({ title: 'Success', description: 'Job created' });
              if (gridData && response.data) {
                setGridData(prev => ({
                  ...prev,
                  jobs: [...(prev.jobs || []), response.data],
                }));
              }
              loadDraftBatches();
              setSelectedJob(response.data);
              setEditForm({
                site_id: response.data.site_id,
                service_id: response.data.service_id,
                event_type: response.data.event_type,
                notes: response.data.notes || '',
                client_reference: response.data.client_reference || '',
              });
              if (response.data.event_type === 'CLIENT_JOB') {
                try { await loadJobPricingLines(response.data.id); } catch { /* noop */ }
              }
            } catch (retryErr) {
              toast({
                title: 'Error',
                description: getApiError(retryErr, 'Failed to create job'),
                variant: 'destructive',
              });
            }
          },
        });
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to create job'),
          variant: 'destructive'
        });
      }
    }

    setActiveCell(null);
    setSearchQuery('');
  };
  
  // Create leave event
  const handleCreateLeave = async (eventType, customDescription = null) => {
    if (!activeCell) return;
    
    // For LEAVE_OTHER, show dialog to get description if not provided
    if (eventType === 'LEAVE_OTHER' && !customDescription) {
      setOtherLeaveDialogOpen(true);
      setOtherLeaveDescription('');
      return;
    }
    
    try {
      const payload = {
        auditor_ids: [activeCell.auditorId],
        scheduled_date: format(activeCell.date, 'yyyy-MM-dd'),
        event_type: eventType
      };
      
      // Add notes for LEAVE_OTHER
      if (eventType === 'LEAVE_OTHER' && customDescription) {
        payload.notes = customDescription;
      }
      
      const response = await api.post('/batch-schedule/job', payload);
      
      toast({ title: 'Success', description: 'Leave event created' });
      
      // Optimistically add the new leave event to gridData instead of full reload
      if (gridData && response.data) {
        setGridData(prev => ({
          ...prev,
          jobs: [...(prev.jobs || []), response.data]
        }));
      }
      
      // Refresh batch counts for real-time Submit button update (Phase 2A)
      loadDraftBatches();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to create leave event'),
        variant: 'destructive'
      });
    }
    
    setActiveCell(null);
    setOtherLeaveDialogOpen(false);
  };
  
  // Load pricing lines for a job
  const loadJobPricingLines = useCallback(async (jobId) => {
    if (!jobId) return;
    setLoadingPricing(true);
    try {
      const response = await api.get(`/batch-schedule/job/${jobId}/pricing-lines`);
      setJobPricingLines(response.data.pricing_lines || []);
    } catch (err) {
      console.error('Failed to load pricing lines:', err);
      setJobPricingLines([]);
    } finally {
      setLoadingPricing(false);
    }
  }, []);
  
  // Calculate total forecast from pricing lines
  const calculateForecastTotal = useMemo(() => {
    return jobPricingLines.reduce((sum, line) => sum + (line.price || 0), 0);
  }, [jobPricingLines]);
  
  // Handle job click (open side panel)
  const handleJobClick = async (job, e) => {
    e.stopPropagation();
    setSelectedJob(job);
    setEditForm({
      site_id: job.site_id,
      service_id: job.service_id,
      event_type: job.event_type,
      notes: job.notes || '',
      client_reference: job.client_reference || '',
      scheduled_date: job.scheduled_date,
      end_date: job.end_date || null,
      // iter358 — seed auditor picker on side panel from canonical M2M ids.
      auditor_ids: Array.isArray(job.auditor_ids)
        ? [...job.auditor_ids]
        : (job.auditors || []).map(a => a.id),
    });
    setActiveCell(null);
    
    if (job.event_type === 'CLIENT_JOB') {
      await loadJobPricingLines(job.id);
    } else {
      setJobPricingLines([]);
    }
  };
  
  // Pricing line management
  const openPricingDialog = (line = null) => {
    setEditingPricingLine(line);
    setPricingForm({
      service_id: line?.service_id?.toString() || '',
      price: line?.price?.toString() || '',
      currency: line?.currency || (region === 'UAE' ? 'AED' : 'GBP'),
      description: line?.description || ''
    });
    setPricingDialogOpen(true);
  };
  
  const handleSavePricingLine = async () => {
    if (!selectedJob || !pricingForm.service_id || !pricingForm.price) {
      toast({ title: 'Error', description: 'Service and price are required', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        service_id: parseInt(pricingForm.service_id),
        price: parseFloat(pricingForm.price),
        currency: pricingForm.currency,
        description: pricingForm.description || null
      };
      
      if (editingPricingLine) {
        await api.put(`/batch-schedule/job/${selectedJob.id}/pricing-lines/${editingPricingLine.id}`, data);
        toast({ title: 'Success', description: 'Pricing line updated' });
      } else {
        await api.post(`/batch-schedule/job/${selectedJob.id}/pricing-lines`, data);
        toast({ title: 'Success', description: 'Pricing line added' });
      }
      
      await loadJobPricingLines(selectedJob.id);
      setPricingDialogOpen(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save pricing line'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeletePricingLine = async (lineId) => {
    if (!selectedJob) return;
    if (!window.confirm('Delete this pricing line?')) return;
    
    const endpoint = `/batch-schedule/job/${selectedJob.id}/pricing-lines/${lineId}`;
    try {
      await api.delete(endpoint);
      toast({ title: 'Success', description: 'Pricing line deleted' });
      await loadJobPricingLines(selectedJob.id);
    } catch (err) {
      const status = err.response?.status || 'unknown';
      const serverMsg = err.response?.data?.detail || err.message || 'Unknown error';
      console.error(`[DELETE FAILED] ${endpoint}`, { status, response: err.response?.data, error: err });
      toast({
        title: 'Error',
        description: `Delete failed (HTTP ${status}): ${serverMsg}`,
        variant: 'destructive'
      });
    }
  };
  
  // Save job changes
  const handleSaveJob = async () => {
    if (!selectedJob || !editForm) return;
    
    setSaving(true);
    try {
      const cleanData = {
        ...editForm,
        scheduled_date: editForm.scheduled_date || selectedJob.scheduled_date
      };
      
      if (editForm.end_date) {
        cleanData.end_date = editForm.end_date;
        cleanData.clear_end_date = false;
      } else {
        cleanData.end_date = null;
        cleanData.clear_end_date = true;
      }
      
      const response = await api.put(`/batch-schedule/job/${selectedJob.id}`, cleanData);
      
      // Optimistic update: Update local state immediately instead of full reload
      setGridData(prev => updateGridDataLocally(prev, selectedJob.id, response.data));
      
      toast({ title: 'Success', description: 'Job updated' });
      setSelectedJob(null);
      setEditForm(null);
      setJobPricingLines([]);
    } catch (err) {
      if (isLockedError(err)) {
        showLockedToast(toast, err);
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to update job'),
          variant: 'destructive'
        });
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Delete job (hard delete - permanently removes from database)
  const handleDeleteJob = async () => {
    if (!selectedJob || saving) return;

    // iter415 + iter470 — Only regular Schedulers (not Senior Schedulers,
    // not Admins) propose deletes. If any of the user's roles is
    // Senior Scheduler, Senior-Scheduler rules apply — including the
    // iter468 immediate hard-delete (deletion never needs Publish or
    // approval).
    const isProposalMode = isScheduler && !isSeniorScheduler && !isAdmin;
    if (isProposalMode) {
      try {
        await api.post(`/jobs/${selectedJob.id}/propose-delete`);
        toast({
          title: 'Deletion Proposed',
          description: 'Job deletion saved as draft for senior-scheduler approval',
        });
        setSelectedJob(null);
        setEditForm(null);
        loadDraftBatches();
      } catch (err) {
        const status = err.response?.status || 'unknown';
        const serverMsg = err.response?.data?.detail || err.message || 'Unknown error';
        toast({
          title: 'Error',
          description: `Propose-delete failed (HTTP ${status}): ${serverMsg}`,
          variant: 'destructive',
        });
      }
      return;
    }

    // Show appropriate warning based on job status
    const isCompleted = selectedJob.status === 'COMPLETED';
    const warningMessage = isCompleted
      ? 'This job is marked as COMPLETED. Deleting it will permanently remove it from all views and reports. This cannot be undone. Are you sure?'
      : 'This will permanently delete the job and remove it from all views and reports. This cannot be undone. Are you sure?';
    
    if (!window.confirm(warningMessage)) return;
    
    const jobIdToDelete = selectedJob.id;
    setSaving(true);
    // Use the standard jobs API endpoint for deletion
    const endpoint = `/jobs/${jobIdToDelete}`;
    try {
      await api.delete(endpoint);
      
      // Optimistic update: Remove from local state instead of full reload
      setGridData(prev => removeJobFromGridLocally(prev, jobIdToDelete));
      
      // Refresh batch counts for real-time Submit button update (Phase 2A)
      loadDraftBatches();
      
      toast({ title: 'Success', description: 'Job permanently deleted' });
      setSelectedJob(null);
      setEditForm(null);
    } catch (err) {
      const status = err.response?.status || 'unknown';
      const serverMsg = err.response?.data?.detail || err.message || 'Unknown error';
      console.error(`[DELETE FAILED] ${endpoint}`, { status, response: err.response?.data, error: err });
      if (isLockedError(err)) {
        showLockedToast(toast, err);
      } else {
        toast({
          title: 'Error',
          description: `Delete failed (HTTP ${status}): ${serverMsg}`,
          variant: 'destructive'
        });
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Legacy alias - calls the same delete function
  const handleCancelJob = handleDeleteJob;
  
  // Copy job
  const handleCopyJob = (job, e) => {
    e.stopPropagation();
    setCopiedJob(job);
    toast({ title: 'Copied', description: 'Click on a cell to add this job to that date' });
  };
  
  // Paste/Extend job
  const handlePasteJob = async (auditorId, date) => {
    if (!copiedJob || copying) return;
    
    setCopying(true);
    const endpoint = `/batch-schedule/job/${copiedJob.id}/extend-to`;
    const payload = {
      target_date: format(date, 'yyyy-MM-dd'),
      target_auditor_id: auditorId
    };
    
    try {
      const response = await api.post(endpoint, payload);
      
      // Optimistic update: Add new job to local grid or update existing
      if (response.data?.new_job) {
        setGridData(prev => addJobToGridLocally(prev, response.data.new_job));
      } else if (response.data?.updated_job) {
        setGridData(prev => updateGridDataLocally(prev, response.data.updated_job.id, response.data.updated_job));
      } else {
        // Fallback: Full reload if response format unexpected
        await loadGridData();
      }
      
      toast({ title: 'Success', description: 'Job extended to new date/auditor' });
      setCopiedJob(null);
    } catch (err) {
      const status = err.response?.status || 'unknown';
      const serverMsg = err.response?.data?.detail || err.message || 'Unknown error';
      console.error(`[COPY FAILED] POST ${endpoint}`, { payload, status, response: err.response?.data, error: err });
      toast({
        title: 'Error',
        description: `Copy failed (HTTP ${status}): ${serverMsg}`,
        variant: 'destructive'
      });
    } finally {
      setCopying(false);
    }
  };


  // Drag handlers
  const handleDragStart = (e, job, auditorId, side = 'end') => {
    // iter340 v8e — block drag-and-drop for locked jobs unless caller is Senior Scheduler / Admin.
    if (job?.is_locked && !(isSeniorScheduler || isAdmin)) {
      e.preventDefault();
      toast({
        title: 'Job locked',
        description: 'Job locked. Contact a Senior Scheduler to request a change.',
        variant: 'destructive'
      });
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job.id.toString());
    const isLeave = job.event_type !== 'CLIENT_JOB';
    setDragState({
      jobId: job.id,
      originalEndDate: job.end_date || job.scheduled_date,
      startDate: job.scheduled_date,
      auditorId,
      isLeave,
      side
    });
  };

  const handleDragOver = (e, date) => {
    e.preventDefault();
    if (dragState) {
      const dateStr = format(date, 'yyyy-MM-dd');
      setDragPreview(dateStr);
    }
  };

  const handleDragEnd = () => {
    setDragState(null);
    setDragPreview(null);
  };

  const handleDrop = async (e, date) => {
    e.preventDefault();
    if (!dragState) return;

    const targetDate = format(date, 'yyyy-MM-dd');
    
    const job = gridData?.jobs?.find(j => j.id === dragState.jobId);
    if (!job) return;

    const auditorId = dragState.auditorId;
    
    let currentStart, currentEnd;
    if (job.assignments && job.assignments.length > 0) {
      // Get all assignments for this auditor (supports multiple date ranges)
      const auditorAssignments = job.assignments.filter(a => a.auditor_id === auditorId);
      if (auditorAssignments.length > 0) {
        // Find the assignment that contains the drag start point
        // For dragging, we work with the earliest start and latest end across all assignments
        currentStart = auditorAssignments.reduce((min, a) => a.start_date < min ? a.start_date : min, auditorAssignments[0].start_date);
        currentEnd = auditorAssignments.reduce((max, a) => a.end_date > max ? a.end_date : max, auditorAssignments[0].end_date);
      } else {
        currentStart = job.scheduled_date;
        currentEnd = job.end_date || job.scheduled_date;
      }
    } else {
      currentStart = job.scheduled_date;
      currentEnd = job.end_date || job.scheduled_date;
    }
    
    const direction = dragState.side || 'end';
    
    if (direction === 'end') {
      if (targetDate < currentStart) {
        toast({
          title: 'Invalid',
          description: 'End date cannot be before start date',
          variant: 'destructive'
        });
        handleDragEnd();
        return;
      }
      if (targetDate === currentEnd) {
        handleDragEnd();
        return;
      }
    } else if (direction === 'start') {
      if (targetDate > currentEnd) {
        toast({
          title: 'Invalid',
          description: 'Start date cannot be after end date',
          variant: 'destructive'
        });
        handleDragEnd();
        return;
      }
      if (targetDate === currentStart) {
        handleDragEnd();
        return;
      }
    }

    try {
      const response = await api.post(`/batch-schedule/job/${dragState.jobId}/extend-auditor`, {
        auditor_id: auditorId,
        new_end_date: targetDate
      });
      
      // Optimistic update: Update the job locally
      if (response.data) {
        setGridData(prev => updateGridDataLocally(prev, dragState.jobId, {
          end_date: targetDate,
          ...(direction === 'start' ? { scheduled_date: targetDate } : {})
        }));
      }
      
      const isLeaveEvent = dragState.isLeave;
      const entityName = isLeaveEvent ? 'Leave' : 'Job';
      
      const action = (direction === 'end') 
        ? (targetDate > currentEnd ? 'extended' : 'shortened')
        : (targetDate < currentStart ? 'extended backward' : 'shortened');
      toast({ title: 'Success', description: `${entityName} ${action} for this auditor` });
    } catch (err) {
      if (isLockedError(err)) {
        showLockedToast(toast, err);
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to resize'),
          variant: 'destructive'
        });
      }
    }
    
    handleDragEnd();
  };
  
  // Navigate months
  const goToPreviousMonth = () => {
    if (filterDateRange === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(new Date(year, month - 2, 1));
    }
    setSelectedJob(null);
    setActiveCell(null);
  };
  
  const goToNextMonth = () => {
    if (filterDateRange === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(new Date(year, month, 1));
    }
    setSelectedJob(null);
    setActiveCell(null);
  };
  
  // Format currency
  const formatCurrency = useCallback((amount, pricingCurrency = null) => {
    if (amount == null) return '-';
    
    const currency = pricingCurrency || (region === 'UAE' ? 'AED' : 'GBP');
    const locale = region === 'UAE' ? 'en-AE' : 'en-GB';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }, [region]);
  
  // Render job block in cell
  const renderJobBlock = (job, auditorId, date, isStart) => {
    const eventInfo = getEventTypeInfo(job.event_type);
    const dateStr = format(date, 'yyyy-MM-dd');
    // Get duration for this specific date block (for multi-block support)
    const duration = getJobDurationForAuditor(job, auditorId, dateStr);
    const isLeave = job.event_type !== 'CLIENT_JOB';
    
    const isDragging = dragState?.jobId === job.id;
    const isInDragRange = isDragging && dragPreview && dateStr >= job.scheduled_date && dateStr <= dragPreview;
    const willBeRemoved = isDragging && dragPreview && dragPreview < (job.end_date || job.scheduled_date) && dateStr > dragPreview;
    
    const clientColour = !isLeave && job.client_colour;
    const isDraft = job.batch_status === 'draft';
    const isLocked = !!job.is_locked;
    const customStyle = clientColour ? {
      backgroundColor: `${clientColour}20`,
      borderColor: clientColour,
      borderLeftWidth: '4px'
    } : {};
    
    // Add draft indicator style (dashed border)
    if (isDraft && !isLeave) {
      customStyle.borderStyle = 'dashed';
      if (!clientColour) {
        customStyle.borderColor = '#9333ea'; // Purple for draft
        customStyle.backgroundColor = '#f3e8ff'; // Light purple bg
      }
    }

    // iter340 v8e — locked jobs override with the amber 3px border so the
    // status reads at-a-glance even when client colours are configured.
    if (isLocked) {
      customStyle.borderStyle = 'solid';
      customStyle.borderColor = '#f59e0b';
      customStyle.borderWidth = '3px';
    }
    
    // Site filter: dim job blocks whose site is not in the active filter.
    const isSiteFiltered = filterSiteIds.length > 0 && job.site_id && !filterSiteIds.includes(job.site_id);

    if (!isStart) {
      return (
        <div
          key={`${job.id}-${dateStr}`}
          className={`job-block h-8 ${clientColour ? 'border' : eventInfo.color} border-l-0 border-r border-t border-b cursor-pointer ${
            willBeRemoved ? 'opacity-30 line-through' : isSiteFiltered ? 'opacity-20' : 'opacity-70'
          } ${isInDragRange ? 'ring-2 ring-blue-400' : ''}`}
          style={clientColour ? { backgroundColor: `${clientColour}15`, borderColor: clientColour } : {}}
          onClick={(e) => handleJobClick(job, e)}
          data-testid={`batch-job-${job.id}-continuation-${dateStr}`}
          title={`${isLeave ? eventInfo.label : job.site_name} (continued)${willBeRemoved ? ' - will be removed' : ''}`}
        >
          <span className="text-[9px] px-1 truncate block">{isLeave ? '' : '...'}</span>
        </div>
      );
    }
    
    return (
      <div
        key={`${job.id}-${dateStr}`}
        className={`job-block relative px-1 py-0.5 text-xs ${clientColour ? 'border rounded' : eventInfo.color + ' border'} cursor-pointer hover:opacity-90 transition-opacity ${
          selectedJob?.id === job.id ? 'ring-2 ring-blue-500' : ''
        } ${isDragging ? 'opacity-50' : ''} ${isSiteFiltered ? 'opacity-20 saturate-0' : ''}`}
        style={{ minHeight: '32px', zIndex: selectedJob?.id === job.id ? 10 : 1, ...customStyle }}
        onClick={(e) => handleJobClick(job, e)}
        data-testid={`batch-job-${job.id}`}
      >
        <div
          className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize flex items-center justify-center hover:bg-black/10 rounded-l"
          draggable
          onDragStart={(e) => handleDragStart(e, job, auditorId, 'start')}
          onDragEnd={handleDragEnd}
          title="Drag left to extend start, right to shrink"
        >
          <GripHorizontal className="h-3 w-3" />
        </div>
        
        <div className="flex items-center gap-1 ml-3">
          <eventInfo.icon className="h-3 w-3 flex-shrink-0" />
          <span className="truncate font-medium text-[10px]">
            {isLeave 
              ? (job.event_type === 'LEAVE_OTHER' && job.notes ? job.notes : eventInfo.label) 
              : (job.site_name || 'No site')}
          </span>
          {/* iter340 v8e — Padlock for locked jobs */}
          {isLocked && (
            <Lock
              className="h-3 w-3 text-amber-700 flex-shrink-0"
              data-testid={`batch-job-lock-icon-${job.id}`}
              aria-label="Locked"
            />
          )}
          {/* Draft indicator badge */}
          {isDraft && !isLeave && (
            <span className="px-1 py-0 text-[8px] bg-purple-600 text-white rounded-sm" data-testid="draft-badge">D</span>
          )}
        </div>
        {!isLeave && job.service_name && (
          <div className="text-[9px] opacity-70 truncate ml-3">{job.service_name}</div>
        )}
        {duration > 1 && (
          <div className="text-[9px] opacity-70 ml-3">{duration}d</div>
        )}
        
        <button
          className="absolute top-0.5 right-5 p-0.5 hover:bg-white/50 rounded"
          onClick={(e) => handleCopyJob(job, e)}
          title="Copy job (Ctrl+C when selected)"
          data-testid={`copy-job-${job.id}`}
        >
          <Copy className="h-3 w-3" />
        </button>
        
        <div
          className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize flex items-center justify-center hover:bg-black/10 rounded-r"
          draggable
          onDragStart={(e) => handleDragStart(e, job, auditorId, 'end')}
          onDragEnd={handleDragEnd}
          title="Drag right to extend, left to shrink"
        >
          <GripHorizontal className="h-3 w-3" />
        </div>
      </div>
    );
  };
  
  if (loading && !gridData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const auditors = gridData?.auditors || [];
  
  return (
    <div className="flex h-full overflow-hidden" data-testid="batch-entry-page">
      {/* Main Grid Area */}
      <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden">
        {/* Fixed Header */}
        <div className="sticky top-0 z-30 bg-background border-b p-4 flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={goToPreviousMonth} data-testid="prev-month-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold" data-testid="grid-month-title">
                {filterDateRange === 'week' 
                  ? `${format(daysToShow[0], 'd MMM')} - ${format(daysToShow[daysToShow.length - 1], 'd MMM yyyy')}`
                  : format(currentDate, 'MMMM yyyy')
                }
              </h2>
              <Button variant="outline" size="icon" onClick={goToNextMonth} data-testid="next-month-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
              {/* iter472 — Prominent Month/Week toggle next to the arrows.
                  Custom range remains available in the Filters popover.
                  Choice persists per user (localStorage). */}
              <div className="ml-2 inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="view-mode-toggle">
                <button
                  type="button"
                  onClick={() => setFilterDateRange('month')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${filterDateRange === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="view-mode-month"
                  aria-pressed={filterDateRange === 'month'}
                >
                  Month
                </button>
                <button
                  type="button"
                  onClick={() => setFilterDateRange('week')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${filterDateRange === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="view-mode-week"
                  aria-pressed={filterDateRange === 'week'}
                >
                  Week
                </button>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {/* Region Filter */}
              <RegionFilter value={regionId} onChange={setRegionId} />
              
              {/* Filter Toggle Button */}
              <Button 
                variant={showFilters ? "default" : "outline"} 
                size="sm" 
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4 mr-1" />
                Filters
              </Button>

              {/* iter397 — Lock Schedule (Senior Scheduler / Admin only). */}
              {(isSeniorScheduler || isAdmin) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openLockDialog}
                  data-testid="open-lock-dialog-btn"
                  title="Lock a date range so future changes by Senior/Admin trigger email notifications"
                >
                  <Lock className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Lock Schedule</span>
                </Button>
              )}
              {activeLocks.length > 0 && (
                <Badge variant="secondary" data-testid="active-locks-badge" className="text-xs">
                  {activeLocks.length} active lock{activeLocks.length === 1 ? '' : 's'}
                </Badge>
              )}

              {/* iter360 — Add Entry. Same dialog as the main Schedule list page.
                  Navigates to /schedule?action=add which auto-opens the
                  Create Job dialog so users can enter complex jobs without
                  leaving the batch-entry context. */}
              {(isScheduler || isSeniorScheduler || isAdmin) && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate('/schedule?action=add&return=batch')}
                  data-testid="batch-add-entry-btn"
                  title="Open the full Add Entry dialog (Schedule view) for complex jobs"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Add Entry</span>
                </Button>
              )}
              
              {/* Submit for Approval — non-SS Schedulers only (SS/Admin use Approve & Publish dialog) */}
              {draftBatches.length > 0 && isScheduler && !isSeniorScheduler && !isAdmin && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="default" 
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="submit-batch-btn"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Submit ({draftBatches.reduce((s, b) => s + (b.job_count || 0) + (b.proposal_count || 0), 0)} items)
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96" align="end">
                    <div className="space-y-3">
                      <h4 className="font-medium">Submit Batches for Approval</h4>
                      <p className="text-sm text-muted-foreground">
                        Once submitted, you cannot edit these jobs until approved or returned.
                      </p>

                      {draftBatches.length > 0 && (
                        <div className="space-y-2">
                          {draftBatches.map(batch => (
                            <div key={batch.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                              <div>
                                <div className="text-sm font-medium">{batch.name || `Batch #${batch.id}`}</div>
                                <div className="text-xs text-muted-foreground">
                                  {batch.job_count > 0 && `${batch.job_count} job(s)`}
                                  {batch.job_count > 0 && batch.proposal_count > 0 && ', '}
                                  {batch.proposal_count > 0 && `${batch.proposal_count} change(s)`}
                                </div>
                              </div>
                              <div className="flex gap-1">
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCancelDraft(batch.id, batch.name)}
                              disabled={cancellingBatch}
                              data-testid={`cancel-draft-${batch.id}`}
                            >
                              {cancellingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handlePreviewNotification(batch.id, batch.name)}
                              disabled={previewLoading || (batch.job_count === 0 && batch.proposal_count === 0)}
                              data-testid={`preview-batch-${batch.id}`}
                            >
                              {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSubmitBatch(batch.id)}
                              disabled={submittingBatch || (batch.job_count === 0 && batch.proposal_count === 0)}
                              data-testid={`submit-batch-${batch.id}`}
                            >
                              {submittingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Submit'}
                            </Button>
                          </div>
                        </div>
                      ))}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              
              {/* Returned Batches Button - Shown for Schedulers with returned batches */}
              {/* Returned Batches Button — visible to Schedulers and Senior
                  Schedulers (with or without Admin). Pure Admins bypass. (iter467) */}
              {returnedBatches.length > 0 && !isPureAdmin && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                      data-testid="returned-batches-btn"
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Returned ({returnedBatches.length})
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96" align="end">
                    <div className="space-y-3">
                      <h4 className="font-medium text-amber-700">Batches Returned for Revision</h4>
                      <p className="text-sm text-muted-foreground">
                        These batches need your attention. Review comments and resubmit when ready.
                      </p>
                      {returnedBatches.map(batch => (
                        <div key={batch.id} className="p-3 bg-amber-50 border border-amber-200 rounded space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium">{batch.name || `Batch #${batch.id}`}</div>
                              <div className="text-xs text-muted-foreground">
                                {batch.job_count} job(s) • Returned by {batch.returned_by_name}
                              </div>
                            </div>
                          </div>
                          {batch.return_comments && (
                            <div className="text-sm bg-white p-2 rounded border border-amber-200">
                              <span className="font-medium text-amber-800">Comments: </span>
                              <span className="text-slate-700">{batch.return_comments}</span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1"
                              onClick={() => openReturnedBatchDialog(batch)}
                              data-testid={`view-returned-batch-${batch.id}`}
                            >
                              View Details
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCancelDraft(batch.id, batch.name)}
                              disabled={cancellingBatch}
                              data-testid={`cancel-returned-${batch.id}`}
                            >
                              {cancellingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Cancel'}
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700 flex-1"
                              onClick={() => handleSubmitBatch(batch.id)}
                              disabled={submittingBatch || batch.job_count === 0}
                              data-testid={`resubmit-batch-${batch.id}`}
                            >
                              {submittingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Resubmit'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              
              {/* Approve & Publish — single entry point for SS/Admin.
                  Replaces the former separate Review and Publish buttons. */}
              {(isSeniorScheduler || isAdmin) && (
                <Button
                  variant="default"
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setApDialogOpen(true)}
                  data-testid="approve-publish-btn"
                >
                  <ClipboardCheck className="h-4 w-4 mr-1" />
                  Approve &amp; Publish
                  {pendingJobCount !== null && pendingJobCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-white/25 text-white text-[10px] font-bold">
                      {pendingJobCount}
                    </span>
                  )}
                </Button>
              )}
              
              {copiedJob && (
                <Badge variant="secondary" className="gap-1">
                  <Copy className="h-3 w-3" />
                  Job copied - click cell or Ctrl+V to extend
                  <button onClick={() => setCopiedJob(null)} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {selectedCell && !copiedJob && (
                <Badge variant="outline" className="gap-1 text-xs">
                  Cell: {format(selectedCell.date, 'd MMM')}
                </Badge>
              )}
              <Badge variant="outline" data-testid="grid-auditor-count">{filteredAuditors.length} auditors</Badge>
              <Badge variant="outline" data-testid="grid-job-count">{gridData?.jobs?.length || 0} jobs</Badge>
              <Badge variant="outline" className="text-xs bg-slate-100" title="Click any cell in the grid, then use the keyboard.">
                ⌨️ Ctrl+C/V copy·paste · Del delete · ↑↓←→ scroll · PgUp/PgDn page · Home/End first·last · Ctrl+Home/End edges
              </Badge>
            </div>
          </div>
          
          {/* Filter Row - Collapsible */}
          {showFilters && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border flex flex-wrap gap-4 items-end" data-testid="filters-panel">
              {/* ── Site / Client Filter ─────────────────────────────── */}
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Client / Site
                </Label>
                <Popover onOpenChange={(open) => { if (!open) setSiteSearchQuery(''); }}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[240px] justify-between"
                      data-testid="filter-site-select"
                    >
                      <span className="truncate">
                        {filterSiteIds.length === 0
                          ? 'All Sites'
                          : filterSiteIds.length === 1
                            ? availableSites.find(s => s.id === filterSiteIds[0])?.name || '1 selected'
                            : `${filterSiteIds.length} sites selected`}
                      </span>
                      <Building2 className="h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    {/* Keyboard search input */}
                    <div className="flex items-center border-b px-3 py-2 gap-2">
                      <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                      <input
                        autoFocus
                        placeholder="Search client or site…"
                        value={siteSearchQuery}
                        onChange={(e) => setSiteSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setSiteSearchQuery('');
                        }}
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        data-testid="site-search-input"
                      />
                      {siteSearchQuery && (
                        <button onClick={() => setSiteSearchQuery('')} className="text-muted-foreground hover:text-foreground">
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    {/* Select All / Clear All */}
                    <div className="flex gap-1 px-2 pt-2 pb-1 border-b">
                      <Button
                        variant="ghost" size="sm"
                        className="flex-1 h-7 text-xs"
                        onClick={() => setFilterSiteIds(availableSites.map(s => s.id))}
                        data-testid="site-select-all"
                      >Select All</Button>
                      <Button
                        variant="ghost" size="sm"
                        className="flex-1 h-7 text-xs"
                        onClick={() => setFilterSiteIds([])}
                        data-testid="site-clear-all"
                      >Clear All</Button>
                    </div>

                    {/* Site list */}
                    <div className="flex flex-col max-h-[260px] overflow-y-auto py-1">
                      {filteredSiteOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No sites match</p>
                      ) : (
                        filteredSiteOptions.map(site => (
                          <label
                            key={site.id}
                            className="flex items-start gap-2 px-3 py-1.5 hover:bg-slate-100 cursor-pointer"
                            data-testid={`site-option-${site.id}`}
                          >
                            <Checkbox
                              checked={filterSiteIds.includes(site.id)}
                              onCheckedChange={(checked) => {
                                setFilterSiteIds(prev =>
                                  checked ? [...prev, site.id] : prev.filter(id => id !== site.id)
                                );
                              }}
                              className="mt-0.5 shrink-0"
                            />
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm leading-tight truncate">{site.name}</span>
                              {site.client_name && (
                                <span className="text-[11px] text-muted-foreground truncate">{site.client_name}</span>
                              )}
                            </div>
                          </label>
                        ))
                      )}
                    </div>

                    {/* Selected count badge */}
                    {filterSiteIds.length > 0 && (
                      <div className="border-t px-3 py-1.5 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{filterSiteIds.length} selected</span>
                        <button
                          className="text-xs text-destructive hover:underline"
                          onClick={() => setFilterSiteIds([])}
                        >Clear</button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* ── Auditor Filter ────────────────────────────────────── */}
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" /> Auditors
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-[220px] justify-between"
                      data-testid="filter-auditor-select"
                    >
                      <span className="truncate">
                        {filterAuditorIds.length === 0 
                          ? 'All Auditors' 
                          : filterAuditorIds.length === 1
                            ? auditors.find(a => a.id === filterAuditorIds[0])?.first_name || '1 selected'
                            : `${filterAuditorIds.length} auditors selected`}
                      </span>
                      <Users className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[220px] p-2" align="start">
                    <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                      {/* Select All / Clear All */}
                      <div className="flex gap-2 mb-2 pb-2 border-b">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="flex-1 h-8 text-xs"
                          onClick={() => setFilterAuditorIds(auditors.map(a => a.id))}
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="flex-1 h-8 text-xs"
                          onClick={() => setFilterAuditorIds([])}
                        >
                          Clear All
                        </Button>
                      </div>
                      {auditors.map(auditor => (
                        <label 
                          key={auditor.id} 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer"
                        >
                          <Checkbox
                            checked={filterAuditorIds.includes(auditor.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFilterAuditorIds(prev => [...prev, auditor.id]);
                              } else {
                                setFilterAuditorIds(prev => prev.filter(id => id !== auditor.id));
                              }
                            }}
                          />
                          <span className="text-sm">{auditor.first_name} {auditor.last_name}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Date Range Filter */}
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Date Range
                </Label>
                <Select value={filterDateRange} onValueChange={setFilterDateRange}>
                  <SelectTrigger className="w-[150px]" data-testid="filter-date-range-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Full Month</SelectItem>
                    <SelectItem value="week">Week View</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Date Range Inputs */}
              {filterDateRange === 'custom' && (
                <>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <Input 
                      type="date" 
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-[150px] !block"
                      data-testid="filter-custom-start"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground">End Date</Label>
                    <Input 
                      type="date" 
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-[150px] !block"
                      min={customStartDate}
                      data-testid="filter-custom-end"
                    />
                  </div>
                </>
              )}
              
              {/* iter393 — Show all auditors vs only those with jobs */}
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" /> Auditor Visibility
                </Label>
                <Select
                  value={showAuditorsWithJobsOnly ? 'with-jobs' : 'all'}
                  onValueChange={(v) => setShowAuditorsWithJobsOnly(v === 'with-jobs')}
                >
                  <SelectTrigger
                    className="w-[200px]"
                    data-testid="filter-auditor-visibility-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value="with-jobs"
                      data-testid="filter-auditor-visibility-with-jobs"
                    >
                      Auditors with jobs only
                    </SelectItem>
                    <SelectItem
                      value="all"
                      data-testid="filter-auditor-visibility-all"
                    >
                      All auditors
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reset Filters Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setFilterAuditorIds([]);
                  setFilterSiteIds([]);
                  setSiteSearchQuery('');
                  setFilterDateRange('month');
                  setCustomStartDate('');
                  setCustomEndDate('');
                  setShowAuditorsWithJobsOnly(true);
                }}
                data-testid="reset-filters-btn"
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        
        {/* Single Table Grid with CSS Sticky - NO JAVASCRIPT SYNC */}
        <div 
          ref={gridContainerRef}
          className="batch-grid-container flex-1 overflow-auto focus:outline-none"
          style={{ maxHeight: 'calc(100vh - 180px)' }}
          tabIndex={0}
          data-testid="batch-grid-container"
        >
          <table className="text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}>
            <thead className="sticky top-0 z-20">
              <tr>
                {/* Date Column Header - Sticky top-left corner */}
                <th 
                  className="sticky left-0 z-30 bg-slate-100 border-b border-r p-1.5 text-left font-medium text-xs"
                  style={{ minWidth: '90px', width: '90px', boxShadow: '2px 2px 5px -2px rgba(0,0,0,0.15)' }}
                  data-testid="date-column-header"
                >
                  Date
                </th>
                {/* Auditor Column Headers */}
                {filteredAuditors.map(auditor => (
                  <th 
                    key={auditor.id}
                    className="bg-slate-100 border-b border-r p-1.5 text-center"
                    style={{ width: '128px', minWidth: '128px', maxWidth: '128px' }}
                    data-testid={`auditor-col-${auditor.id}`}
                  >
                    <div className="font-medium text-slate-800 text-xs">{auditor.first_name}</div>
                    <div className="font-medium text-slate-800 text-[11px]">{auditor.last_name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daysToShow.map(date => {
                const dateStr = format(date, 'yyyy-MM-dd');
                const holiday = getBankHoliday(dateStr);
                const weekend = isWeekend(date);
                
                return (
                  <tr key={dateStr} data-testid={`date-row-${dateStr}`}>
                    {/* Date Cell - Sticky left */}
                    <td 
                      className={`sticky left-0 z-10 border-b border-r p-1.5 text-xs font-medium ${
                        holiday ? 'bg-amber-100' : weekend ? 'bg-slate-200' : 'bg-white'
                      }`}
                      style={{ minWidth: '90px', width: '90px', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.15)' }}
                      data-testid={`date-cell-${dateStr}`}
                    >
                      <div className="font-medium">{format(date, 'EEE d')}</div>
                      {holiday && (
                        <div className="text-[9px] text-amber-700 font-normal truncate" title={holiday.name}>
                          {holiday.name}
                        </div>
                      )}
                    </td>
                    
                    {/* Auditor Cells */}
                    {filteredAuditors.map(auditor => {
                      // iter451 — O(1) lookup via precomputed cellJobsIndex.
                      const cellJobs = getJobsForCellFast(auditor.id, dateStr);
                      const isActive = activeCell?.auditorId === auditor.id && 
                                       isSameDay(activeCell.date, date);
                      const canPaste = copiedJob && cellJobs.length === 0 && !copying;
                      const isDragTarget = dragState && dragState.auditorId === auditor.id;
                      const isSelected = selectedCell?.auditorId === auditor.id && 
                                         isSameDay(selectedCell.date, date);
                      // iter397 — lock detection for this cell
                      const cellLock = isCellLocked(auditor.id, dateStr);
                      
                      return (
                        <td
                          key={auditor.id}
                          // iter451 — Removed `transition-colors` from cells: the
                          // per-cell colour transitions cascade paint work on
                          // every scroll frame, which is the primary cause of
                          // jank in the Batch Matrix. Hover states still apply
                          // instantly (no animation) which is imperceptible in
                          // practice.
                          className={`batch-cell border-b border-r p-1 align-top cursor-pointer relative ${
                            holiday ? 'bg-amber-50' : weekend ? 'bg-slate-100' : 'bg-white'
                          } ${isActive ? 'bg-blue-50 ring-2 ring-blue-500' : ''} ${
                            isSelected && !isActive ? 'ring-1 ring-slate-400' : ''
                          } ${canPaste ? 'hover:bg-green-50' : 'hover:bg-slate-50'
                          } ${isDragTarget && dragPreview === dateStr ? 'bg-blue-100' : ''} ${
                            cellLock ? 'ring-1 ring-amber-400/60' : ''
                          }`}
                          style={{ width: '128px', minWidth: '128px', maxWidth: '128px' }}
                          onClick={(e) => {
                            if (copiedJob && cellJobs.length === 0 && !copying) {
                              handlePasteJob(auditor.id, date);
                            } else if (!copying) {
                              handleCellClick(auditor.id, date, e);
                            }
                          }}
                          onDragOver={(e) => handleDragOver(e, date)}
                          onDrop={(e) => handleDrop(e, date)}
                          data-testid={`cell-${dateStr}-${auditor.id}`}
                          title={cellLock ? `Schedule lock #${cellLock.id} active for this date range — Senior/Admin changes here trigger an audit email.` : undefined}
                        >
                          {/* iter397 — Lock badge */}
                          {cellLock && (
                            <div
                              className="absolute top-0 right-0 p-0.5 text-amber-600 pointer-events-none"
                              data-testid={`cell-lock-badge-${dateStr}-${auditor.id}`}
                              aria-label="Locked cell"
                            >
                              <Lock className="h-3 w-3" />
                            </div>
                          )}
                          {/* Job blocks */}
                          {cellJobs.map(job => {
                            const isStart = isJobStartDate(job, dateStr, auditor.id);
                            return renderJobBlock(job, auditor.id, date, isStart);
                          })}
                          
                          {/* Active cell autocomplete */}
                          {isActive && cellJobs.length === 0 && (
                            <div className="relative" onClick={e => e.stopPropagation()}>
                              <Input
                                ref={searchInputRef}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Type site name..."
                                className="h-8 text-xs"
                                autoFocus
                                data-testid="site-search-input"
                              />
                              
                              {/* Search results dropdown - rendered via portal for proper overflow handling */}
                              {(searchResults.length > 0 || searchLoading) && dropdownPosition && createPortal(
                                <div 
                                  className="fixed bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto"
                                  style={{
                                    left: dropdownPosition.left,
                                    top: dropdownPosition.top,
                                    width: Math.max(dropdownPosition.width, 200),
                                    zIndex: 9999
                                  }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  {searchLoading ? (
                                    <div className="p-2 text-center text-muted-foreground">
                                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                      Searching...
                                    </div>
                                  ) : (
                                    searchResults.map(site => (
                                      <button
                                        key={site.id}
                                        className="w-full px-3 py-2 text-left hover:bg-slate-100 text-xs border-b last:border-b-0"
                                        onClick={() => handleSiteSelect(site)}
                                        data-testid={`site-option-${site.id}`}
                                      >
                                        <div className="font-medium">{site.name}</div>
                                        {site.parent_client_name && (
                                          <div className="text-muted-foreground text-[10px]">
                                            {site.parent_client_name}
                                          </div>
                                        )}
                                        {site.city && (
                                          <div className="text-muted-foreground text-[10px]">{site.city}</div>
                                        )}
                                      </button>
                                    ))
                                  )}
                                </div>,
                                document.body
                              )}
                              
                              {/* Leave event buttons - also rendered via portal */}
                              {searchQuery === '' && dropdownPosition && createPortal(
                                <div 
                                  className="fixed bg-white border rounded-md shadow-lg p-2"
                                  style={{
                                    left: dropdownPosition.left,
                                    top: dropdownPosition.top,
                                    width: Math.max(dropdownPosition.width, 200),
                                    zIndex: 9999
                                  }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <div className="text-[10px] text-muted-foreground mb-2">Or create leave:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {EVENT_TYPES.filter(e => e.value !== 'CLIENT_JOB').map(evt => (
                                      <button
                                        key={evt.value}
                                        className={`px-2 py-1 text-[10px] rounded ${evt.color}`}
                                        onClick={() => handleCreateLeave(evt.value)}
                                        data-testid={`create-${evt.value.toLowerCase()}-btn`}
                                      >
                                        <evt.icon className="h-3 w-3 inline mr-1" />
                                        {evt.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>,
                                document.body
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredAuditors.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No auditors found {filterAuditorIds.length > 0 ? '(check filter)' : 'for this region'}
            </div>
          )}

          {/* iter340 v8e — lock legend */}
          <div className="px-4 py-3">
            <LockLegend />
          </div>
        </div>
      </div>
      
      {/* Side Panel */}
      {selectedJob && (
        <div className="fixed right-0 top-16 bottom-0 w-80 bg-white border-l shadow-lg z-30 overflow-y-auto" data-testid="job-side-panel">
          <div className="p-4 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Edit Job</h3>
              <div className="flex items-center gap-2">
                {/* Batch status indicator - More prominent for draft jobs */}
                {selectedJob.batch_status === 'draft' && (
                  <Badge className="bg-purple-600 text-white border-purple-700 animate-pulse" data-testid="batch-status-badge">
                    DRAFT
                  </Badge>
                )}
                {selectedJob.batch_status === 'pending_approval' && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300" data-testid="batch-status-badge">
                    Pending
                  </Badge>
                )}
                {selectedJob.batch_status === 'returned' && (
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300" data-testid="batch-status-badge">
                    Returned
                  </Badge>
                )}
                {/* iter340 v8e — lock badge in side-panel header */}
                <LockBadge job={selectedJob} />
                <Button variant="ghost" size="icon" onClick={() => { setSelectedJob(null); setEditForm(null); }} data-testid="close-job-panel-btn">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* iter340 v8e — locked banner + lock toggle for Senior Scheduler / Admin */}
            {selectedJob.is_locked && (
              <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded text-xs text-amber-900 flex items-start gap-2" data-testid="job-locked-banner">
                <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <strong>Locked.</strong> Only Senior Scheduler / Admin can edit, move or delete this job.
                  {selectedJob.lock_reason && (
                    <div className="mt-1 italic">{selectedJob.lock_reason}</div>
                  )}
                </div>
              </div>
            )}
            {(isSeniorScheduler || isAdmin) && (
              <div className="mt-2">
                <LockToggleButton
                  job={selectedJob}
                  onChange={(updated) => {
                    setSelectedJob((prev) => prev ? { ...prev, ...updated } : prev);
                    setGridData((prev) => updateGridDataLocally(prev, updated.id, updated));
                  }}
                />
              </div>
            )}
            {/* Draft warning banner for Admin/Senior Scheduler */}
            {selectedJob.batch_status === 'draft' && (isSeniorScheduler || isAdmin) && (
              <div className="mt-2 p-2 bg-purple-100 border border-purple-300 rounded text-xs text-purple-800">
                <strong>Draft Job:</strong> This job is part of an unapproved batch and is not yet in reports.
              </div>
            )}
            {/* Pending Approval banner for reviewers */}
            {selectedJob.batch_status === 'pending_approval' && (isSeniorScheduler || isAdmin) && (
              <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded text-xs text-amber-800">
                <strong>Pending Approval:</strong> This job is part of a batch under review. Use the Review button to approve/return the entire batch.
              </div>
            )}
          </div>
          
          <div className="p-4 space-y-4">
            {/* Event Type */}
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select 
                value={editForm?.event_type || 'CLIENT_JOB'} 
                onValueChange={(value) => setEditForm(prev => ({ ...prev, event_type: value }))}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(evt => (
                    <SelectItem key={evt.value} value={evt.value}>
                      <span className="flex items-center gap-2">
                        <evt.icon className="h-4 w-4" />
                        {evt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Site (for CLIENT_JOB) */}
            {editForm?.event_type === 'CLIENT_JOB' && (
              <>
                <div className="space-y-2">
                  <Label>Site</Label>
                  <div className="p-2 bg-slate-100 rounded text-sm">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {selectedJob.site_name || 'Not selected'}
                    </div>
                  </div>
                </div>
                
                {/* Service Type */}
                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select
                    value={editForm?.service_id?.toString() || ''}
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, service_id: parseInt(value) }))}
                  >
                    <SelectTrigger data-testid="edit-service-select">
                      <SelectValue placeholder="Select service..." />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(svc => (
                        <SelectItem key={svc.id} value={svc.id.toString()}>
                          {svc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* iter358 — Auditor multi-picker on the side panel. Saves via
                    the same PUT /batch-schedule/job/{id} endpoint that the iter357
                    preventive control hooks into, so adding an auditor here also
                    auto-creates a full-span job_assignments row. No risk of
                    Matt-style drift. */}
                <div className="space-y-2" data-testid="side-panel-auditor-section">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Assigned Auditors
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        data-testid="side-panel-auditor-trigger"
                      >
                        <span className="truncate text-left">
                          {(() => {
                            const ids = editForm?.auditor_ids || [];
                            if (ids.length === 0) return <span className="text-amber-600">Unassigned</span>;
                            if (ids.length === 1) {
                              const a = auditors.find(x => x.id === ids[0]);
                              return a ? `${a.first_name} ${a.last_name}` : `${ids.length} selected`;
                            }
                            return `${ids.length} auditors selected`;
                          })()}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="start">
                      <div className="p-2 border-b flex items-center justify-between gap-2 text-xs">
                        <button
                          type="button"
                          className="text-sky-600 hover:underline disabled:opacity-50"
                          disabled={(editForm?.auditor_ids || []).length === auditors.length}
                          onClick={() => setEditForm(prev => ({
                            ...prev,
                            auditor_ids: auditors.map(a => a.id),
                          }))}
                          data-testid="side-panel-auditor-select-all"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="text-slate-600 hover:underline disabled:opacity-50"
                          disabled={(editForm?.auditor_ids || []).length === 0}
                          onClick={() => setEditForm(prev => ({ ...prev, auditor_ids: [] }))}
                          data-testid="side-panel-auditor-clear"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-1">
                        {auditors.map(a => {
                          const checked = (editForm?.auditor_ids || []).includes(a.id);
                          return (
                            <label
                              key={a.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
                              data-testid={`side-panel-auditor-option-${a.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setEditForm(prev => {
                                    const ids = new Set(prev?.auditor_ids || []);
                                    if (e.target.checked) ids.add(a.id); else ids.delete(a.id);
                                    return { ...prev, auditor_ids: Array.from(ids) };
                                  });
                                }}
                              />
                              <span>{a.first_name} {a.last_name}</span>
                            </label>
                          );
                        })}
                        {auditors.length === 0 && (
                          <div className="text-xs text-muted-foreground px-2 py-3 text-center">
                            No auditors available.
                          </div>
                        )}
                      </div>
                      <div className="border-t p-2 text-[11px] text-muted-foreground">
                        Changes are committed when you click <strong>Save Changes</strong>.
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Pricing Lines */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Banknote className="h-4 w-4" />
                      Pricing Lines
                    </Label>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openPricingDialog()}
                      data-testid="add-pricing-line-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  {loadingPricing ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : jobPricingLines.length > 0 ? (
                    <div className="border rounded overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs py-1">Service</TableHead>
                            <TableHead className="text-xs py-1 text-right">Price</TableHead>
                            <TableHead className="text-xs py-1 w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {jobPricingLines.map(line => (
                            <TableRow key={line.id} className="text-sm">
                              <TableCell className="py-1">
                                <div className="font-medium text-xs">{line.service_name}</div>
                                {line.description && (
                                  <div className="text-[10px] text-muted-foreground">{line.description}</div>
                                )}
                              </TableCell>
                              <TableCell className="py-1 text-right font-medium">
                                {formatCurrency(line.price)}
                              </TableCell>
                              <TableCell className="py-1">
                                <div className="flex gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6"
                                    onClick={() => openPricingDialog(line)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => handleDeletePricingLine(line.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="p-3 text-center text-sm text-muted-foreground bg-slate-50 rounded border border-dashed">
                      No pricing lines. Click "Add" to add one.
                    </div>
                  )}
                  
                  {/* Forecast Total */}
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">Forecast Total:</span>
                      <span className="font-bold text-green-700 text-lg">
                        {formatCurrency(calculateForecastTotal)}
                      </span>
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      Sum of {jobPricingLines.length} pricing line{jobPricingLines.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                
                {/* Client Reference */}
                <div className="space-y-2">
                  <Label>Client Reference</Label>
                  <Input
                    value={editForm?.client_reference || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, client_reference: e.target.value }))}
                    placeholder="Optional reference"
                    data-testid="client-reference-input"
                  />
                </div>
              </>
            )}
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm?.notes || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes..."
                rows={3}
                data-testid="notes-input"
              />
            </div>
            
            {/* Dates */}
            <div className="space-y-3 p-3 bg-slate-50 rounded border">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Dates</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Input
                    type="date"
                    value={editForm?.scheduled_date || selectedJob.scheduled_date || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    className="h-9 text-sm !block"
                    data-testid="edit-start-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Input
                    type="date"
                    value={editForm?.end_date || selectedJob.end_date || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, end_date: e.target.value || null }))}
                    className="h-9 text-sm !block"
                    min={editForm?.scheduled_date || selectedJob.scheduled_date}
                    data-testid="edit-end-date"
                  />
                </div>
              </div>
              {selectedJob.job_dates && selectedJob.job_dates.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Non-consecutive dates:</span>{' '}
                  {selectedJob.job_dates.join(', ')}
                </div>
              )}
              {(editForm?.end_date || selectedJob.end_date) && (editForm?.end_date || selectedJob.end_date) !== (editForm?.scheduled_date || selectedJob.scheduled_date) && (
                <Badge variant="outline" className="text-xs">
                  {getJobDuration({ 
                    scheduled_date: editForm?.scheduled_date || selectedJob.scheduled_date,
                    end_date: editForm?.end_date || selectedJob.end_date
                  })} days
                </Badge>
              )}
            </div>
            
            {/* Status indicator */}
            {selectedJob.status && (
              <div className="p-2 rounded text-sm">
                <Label className="text-xs">Status</Label>
                <Badge 
                  variant={selectedJob.status === 'COMPLETED' ? 'default' : 
                           selectedJob.status === 'CANCELLED' ? 'destructive' : 'secondary'}
                  className="mt-1"
                >
                  {selectedJob.status}
                </Badge>
              </div>
            )}
            
            {/* Assigned auditors */}
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <div className="flex flex-wrap gap-1">
                {selectedJob.auditor_ids?.map(auditorId => {
                  const auditor = auditors.find(a => a.id === auditorId);
                  return auditor ? (
                    <Badge key={auditorId} variant="secondary">
                      {auditor.first_name} {auditor.last_name}
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="p-4 border-t sticky bottom-0 bg-white mb-16">
            {/* Hide edit actions for pending_approval batch jobs (read-only view for reviewers and non-owner schedulers) */}
            {selectedJob.batch_status === 'pending_approval' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <div>
                    <strong>Under Review</strong>
                    <p className="text-xs mt-1">
                      {(isSeniorScheduler || isAdmin) 
                        ? 'This job cannot be edited individually. Use the Review button in the toolbar to approve or return the entire batch.'
                        : 'This job is pending approval and cannot be modified until reviewed.'}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => { setSelectedJob(null); setEditForm(null); }}
                >
                  Close
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {selectedJob.status === 'COMPLETED' && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 mb-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>This job is completed. Changes may affect reports.</span>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCancelJob}
                    disabled={saving || selectedJob.status === 'CANCELLED' || (selectedJob.is_locked && !(isSeniorScheduler || isAdmin))}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="cancel-job-btn"
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={handleDeleteJob}
                    disabled={saving || (selectedJob.is_locked && !(isSeniorScheduler || isAdmin))}
                    data-testid="delete-job-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleSaveJob}
                  disabled={saving || (selectedJob.is_locked && !(isSeniorScheduler || isAdmin))}
                  data-testid="save-job-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Click outside to close autocomplete */}
      {activeCell && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setActiveCell(null)}
        />
      )}
      
      {/* Pricing Line Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingPricingLine ? 'Edit Pricing Line' : 'Add Pricing Line'}
            </DialogTitle>
            <DialogDescription>
              {editingPricingLine ? 'Modify the service and price for this line.' : 'Add a service with its price to this job.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Service *</Label>
              <Select 
                value={pricingForm.service_id} 
                onValueChange={(value) => setPricingForm(prev => ({ ...prev, service_id: value }))}
              >
                <SelectTrigger data-testid="pricing-service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id.toString()}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Price ({currencySymbol}) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricingForm.price}
                onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                data-testid="pricing-price-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select 
                value={pricingForm.currency} 
                onValueChange={(value) => setPricingForm(prev => ({ ...prev, currency: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="AED">AED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={pricingForm.description}
                onChange={(e) => setPricingForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Standard rate, Peak season"
                data-testid="pricing-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePricingLine} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editingPricingLine ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Other Leave Description Dialog */}
      <Dialog open={otherLeaveDialogOpen} onOpenChange={setOtherLeaveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Other Leave Event</DialogTitle>
            <DialogDescription>
              Enter a description for this event (e.g., Meeting, Training, Conference)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={otherLeaveDescription}
                onChange={(e) => setOtherLeaveDescription(e.target.value)}
                placeholder="e.g., Team meeting, Training session"
                autoFocus
                data-testid="other-leave-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtherLeaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleCreateLeave('LEAVE_OTHER', otherLeaveDescription)}
              disabled={!otherLeaveDescription.trim()}
            >
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Batch Review Action Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' && 'Approve Batch'}
              {reviewAction === 'return' && 'Return Batch'}
              {reviewAction === 'delete' && 'Delete Batch'}
            </DialogTitle>
            <DialogDescription>
              {selectedBatchForReview && (
                <span className="block mt-2">
                  <span className="font-medium">{selectedBatchForReview.name || `Batch #${selectedBatchForReview.id}`}</span>
                  <span className="block text-xs mt-1">
                    Submitted by {selectedBatchForReview.scheduler_name} • 
                    {selectedBatchForReview.job_count > 0 && ` ${selectedBatchForReview.job_count} job(s)`}
                    {selectedBatchForReview.job_count > 0 && selectedBatchForReview.proposal_count > 0 && ', '}
                    {selectedBatchForReview.proposal_count > 0 && ` ${selectedBatchForReview.proposal_count} change(s)`}
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {reviewAction === 'approve' && (
            <div className="py-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to approve this batch? The jobs will be finalized and visible in reports.
              </p>
              {/* Expandable per-date list — cross-month safe (uses the
                  batch-scoped read, not a month-filtered /grid). Lets the
                  Senior Scheduler see every date in a Block Booking as one
                  coherent unit before approving. */}
              <ApproveDialogDateList batch={selectedBatchForReview} />
            </div>
          )}
          
          {(reviewAction === 'return' || reviewAction === 'delete') && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />
                  {reviewAction === 'return' ? 'Return Comments *' : 'Deletion Reason *'}
                </Label>
                <Textarea
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  placeholder={
                    reviewAction === 'return' 
                      ? 'Explain what needs to be revised...'
                      : 'Explain why this batch is being deleted...'
                  }
                  rows={3}
                  autoFocus
                  data-testid="review-comments-input"
                />
              </div>
              {reviewAction === 'return' && (
                <p className="text-xs text-muted-foreground">
                  The scheduler will be able to edit and resubmit the batch after reviewing your comments.
                </p>
              )}
              {reviewAction === 'delete' && (
                <p className="text-xs text-destructive">
                  Warning: Deleting a batch will remove it from the workflow. Jobs will remain but be orphaned.
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReviewDialogOpen(false)}
              disabled={reviewLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReviewAction}
              disabled={reviewLoading || ((reviewAction === 'return' || reviewAction === 'delete') && !reviewComments.trim())}
              variant={reviewAction === 'delete' ? 'destructive' : 'default'}
              className={reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="confirm-review-action"
            >
              {reviewLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {reviewAction === 'approve' && 'Approve'}
              {reviewAction === 'return' && 'Return for Revision'}
              {reviewAction === 'delete' && 'Delete Batch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Returned Batch Details Dialog */}
      <Dialog open={returnedBatchDialogOpen} onOpenChange={setReturnedBatchDialogOpen}>
        <DialogContent
          className="dialog-resizable"
          style={{ width: '32rem', maxHeight: '70vh' }}
          data-testid="returned-batch-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              Batch Returned for Revision
            </DialogTitle>
            <DialogDescription>
              {selectedReturnedBatch && (
                <span className="block mt-2">
                  <span className="font-medium">{selectedReturnedBatch.name || `Batch #${selectedReturnedBatch.id}`}</span>
                  <span className="block text-xs mt-1">
                    {selectedReturnedBatch.job_count} job(s) • {selectedReturnedBatch.month}/{selectedReturnedBatch.year}
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReturnedBatch && (
            <div className="space-y-4 py-4">
              {/* Return Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-amber-800">
                  <MessageSquare className="h-4 w-4" />
                  <span className="font-medium">Reviewer Comments</span>
                </div>
                <p className="text-sm text-slate-700">
                  {selectedReturnedBatch.return_comments || 'No comments provided'}
                </p>
                <div className="text-xs text-muted-foreground pt-2 border-t border-amber-200">
                  Returned by {selectedReturnedBatch.returned_by_name} on{' '}
                  {selectedReturnedBatch.returned_at 
                    ? new Date(selectedReturnedBatch.returned_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })
                    : 'Unknown date'
                  }
                </div>
              </div>
              
              {/* Instructions */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">What to do next:</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Review the comments from the reviewer above</li>
                  <li>Navigate to the batch's month ({selectedReturnedBatch.month}/{selectedReturnedBatch.year}) in the matrix</li>
                  <li>Edit, add, or remove jobs as needed</li>
                  <li>Resubmit the batch when ready</li>
                </ol>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReturnedBatchDialogOpen(false)}
            >
              Close
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => {
                if (selectedReturnedBatch) {
                  handleSubmitBatch(selectedReturnedBatch.id);
                }
              }}
              disabled={submittingBatch || (selectedReturnedBatch && selectedReturnedBatch.job_count === 0)}
              data-testid="resubmit-from-dialog"
            >
              {submittingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Resubmit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Batch Drilldown Dialog - Shows jobs in a pending batch for review */}
      <Dialog open={batchDrilldownOpen} onOpenChange={setBatchDrilldownOpen}>
        <DialogContent
          className="overflow-hidden flex flex-col dialog-resizable"
          style={{ width: '42rem', maxHeight: '80vh' }}
          data-testid="batch-drilldown-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {selectedBatchForDrilldown?.name || 'Batch Jobs'}
            </DialogTitle>
            <DialogDescription>
              {selectedBatchForDrilldown && (
                <div className="space-y-1">
                  <div>
                    <span className="font-medium">Submitted by:</span> {selectedBatchForDrilldown.scheduler_name}
                  </div>
                  <div>
                    <span className="font-medium">Submitted on:</span> {selectedBatchForDrilldown.submitted_at 
                      ? new Date(selectedBatchForDrilldown.submitted_at).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })
                      : 'Unknown'
                    }
                  </div>
                  <div>
                    <span className="font-medium">Period:</span> {selectedBatchForDrilldown.month}/{selectedBatchForDrilldown.year}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            {loadingBatchJobs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : batchJobs.length === 0 && batchProposals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No jobs or changes found in this batch
              </div>
            ) : (
              <div className="space-y-4">
                {/* New Jobs Section */}
                {batchJobs.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Plus className="h-4 w-4 text-green-600" />
                      New Jobs ({batchJobs.length})
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs">Site</TableHead>
                            <TableHead className="text-xs">Service</TableHead>
                            <TableHead className="text-xs">Auditor(s)</TableHead>
                            <TableHead className="text-xs text-right">Price</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {batchJobs.map(job => {
                            const auditorNames = (job.auditor_ids || []).map(id => {
                              const auditor = batchDrilldownAuditors?.find(a => a.id === id);
                              return auditor ? `${auditor.first_name} ${auditor.last_name}` : `ID:${id}`;
                            });
                            
                            return (
                              <TableRow key={job.id} className="text-sm">
                                <TableCell className="py-2">
                                  <div className="font-medium">
                                    {new Date(job.scheduled_date).toLocaleDateString('en-GB', {
                                      day: 'numeric', month: 'short'
                                    })}
                                  </div>
                                  {job.end_date && job.end_date !== job.scheduled_date && (
                                    <div className="text-xs text-muted-foreground">
                                      to {new Date(job.end_date).toLocaleDateString('en-GB', {
                                        day: 'numeric', month: 'short'
                                      })}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="py-2">
                                  <div className="font-medium">{job.site_name || 'N/A'}</div>
                                </TableCell>
                                <TableCell className="py-2">
                                  {job.service_name || 'N/A'}
                                </TableCell>
                                <TableCell className="py-2">
                                  {auditorNames.length > 0 
                                    ? <span className="text-xs">{auditorNames.join(', ')}</span>
                                    : <span className="text-muted-foreground">Unassigned</span>
                                  }
                                </TableCell>
                                <TableCell className="py-2 text-right">
                                  {job.effective_price != null 
                                    ? `£${Number(job.effective_price).toFixed(2)}`
                                    : '—'
                                  }
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                
                {/* Change Proposals Section */}
                {batchProposals.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <FileEdit className="h-4 w-4 text-purple-600" />
                      Proposed Changes ({batchProposals.length})
                    </h4>
                    <div className="space-y-2">
                      {batchProposals.map(proposal => (
                        <div 
                          key={proposal.id} 
                          className={`border rounded-lg p-3 ${
                            proposal.proposal_type === 'DELETE' 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-purple-50 border-purple-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {proposal.proposal_type === 'DELETE' ? (
                                <Badge variant="destructive" className="text-xs">DELETE</Badge>
                              ) : (
                                <Badge className="bg-purple-600 text-xs">EDIT</Badge>
                              )}
                              <span className="font-medium text-sm">{proposal.job_site_name || 'Unknown Site'}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              Current: {proposal.job_scheduled_date 
                                ? new Date(proposal.job_scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                : 'Unknown'
                              }
                            </span>
                          </div>
                          
                          {/* Current auditors */}
                          {proposal.job_auditors && proposal.job_auditors.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Current auditor(s): {proposal.job_auditors.join(', ')}
                            </div>
                          )}
                          
                          {/* Proposed changes for EDIT */}
                          {proposal.proposal_type === 'EDIT' && proposal.proposed_changes && (
                            <div className="mt-2 text-xs space-y-1">
                              <div className="font-medium text-purple-700">Proposed changes:</div>
                              <ul className="list-disc list-inside text-purple-800 space-y-0.5">
                                {proposal.proposed_changes.scheduled_date && (
                                  <li>Date → {new Date(proposal.proposed_changes.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</li>
                                )}
                                {proposal.proposed_changes.end_date && (
                                  <li>End Date → {new Date(proposal.proposed_changes.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</li>
                                )}
                                {proposal.proposed_auditor_names && proposal.proposed_auditor_names.length > 0 && (
                                  <li>Auditor(s) → {proposal.proposed_auditor_names.join(', ')}</li>
                                )}
                                {proposal.proposed_changes.notes && (
                                  <li>Notes → "{proposal.proposed_changes.notes}"</li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {proposal.proposal_type === 'DELETE' && (
                            <div className="mt-2 text-xs text-red-700">
                              This job will be deleted when the batch is approved.
                            </div>
                          )}
                          
                          <div className="text-xs text-muted-foreground mt-2">
                            Proposed by: {proposal.created_by_name || 'Unknown'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => setBatchDrilldownOpen(false)}
            >
              Close
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setBatchDrilldownOpen(false);
                  openReviewDialog(selectedBatchForDrilldown, 'approve');
                }}
                data-testid="drilldown-approve"
              >
                Approve
              </Button>
              {/* Return button - hidden for auditor-only batches */}
              {selectedBatchForDrilldown && !selectedBatchForDrilldown.scheduler_is_auditor_only && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setBatchDrilldownOpen(false);
                    openReviewDialog(selectedBatchForDrilldown, 'return');
                  }}
                  data-testid="drilldown-return"
                >
                  Return
                </Button>
              )}
              <Button 
                variant="destructive"
                onClick={() => {
                  setBatchDrilldownOpen(false);
                  openReviewDialog(selectedBatchForDrilldown, 'delete');
                }}
                data-testid="drilldown-delete"
              >
                Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Toaster />

      {/* iter431 — Duplicate job confirmation */}
      <DuplicateJobConfirmDialog
        open={!!duplicateConfirm}
        duplicateInfo={duplicateConfirm?.detail}
        onClose={() => setDuplicateConfirm(null)}
        onConfirm={async () => {
          const retry = duplicateConfirm?.retry;
          setDuplicateConfirm(null);
          if (retry) await retry();
        }}
      />

      {/* Notification Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="overflow-hidden flex flex-col dialog-resizable"
          style={{ width: '56rem', maxHeight: '85vh' }}
          data-testid="notification-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>Notification Preview — {previewBatchName}</DialogTitle>
            <DialogDescription>
              {isSeniorScheduler
                ? 'This is how the notification email will appear to everyone once you Publish. Review the changes below before releasing.'
                : 'This is how the notification email will appear to reviewers. Review the changes below before submitting.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-md bg-white">
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full min-h-[400px] border-0"
              style={{ height: '500px' }}
              sandbox=""
            />
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="preview-close-btn">
              Close
            </Button>
            {isSeniorScheduler ? (
              // iter476 — SS Publish flow (submit + approve + release in
              // one click). Regular schedulers keep the standard Submit.
              <Button
                onClick={async () => {
                  setPreviewOpen(false);
                  if (previewBatchId) await handlePublishBatch(previewBatchId);
                }}
                disabled={publishingBatch}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="preview-publish-btn"
              >
                {publishingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Confirm &amp; Publish
              </Button>
            ) : (
              <Button
                onClick={handlePreviewAndSubmit}
                disabled={submittingBatch}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="preview-submit-btn"
              >
                {submittingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Confirm &amp; Submit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter397 — Schedule Lock dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="lock-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" /> Lock Schedule
            </DialogTitle>
            <DialogDescription>
              Lock a date range × auditor set. Schedulers and auditors continue through the existing workflow; any change inside the locked range — including by Senior Scheduler / Admin — will email every Senior Scheduler + Admin (audit trail).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input
                  type="date"
                  value={lockFormStart}
                  onChange={(e) => setLockFormStart(e.target.value)}
                  className="!block"
                  data-testid="lock-start-date"
                />
              </div>
              <div>
                <Label>End date</Label>
                <Input
                  type="date"
                  value={lockFormEnd}
                  onChange={(e) => setLockFormEnd(e.target.value)}
                  className="!block"
                  data-testid="lock-end-date"
                />
              </div>
            </div>
            <div>
              <Label className="flex items-center justify-between">
                <span>
                  Auditors ({lockFormAuditorIds.length === 0
                    ? 'none selected'
                    : lockFormAuditorIds.length === (gridData?.auditors || []).length
                      ? `all ${lockFormAuditorIds.length} selected`
                      : `${lockFormAuditorIds.length} of ${(gridData?.auditors || []).length} selected`})
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setLockFormAuditorIds((gridData?.auditors || []).map(a => a.id))}
                    data-testid="lock-select-all-auditors"
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setLockFormAuditorIds([])}
                    data-testid="lock-deselect-all-auditors"
                  >
                    Deselect all
                  </Button>
                </div>
              </Label>
              <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                {(gridData?.auditors || []).map(a => {
                  const checked = lockFormAuditorIds.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className="flex items-center gap-2 cursor-pointer text-sm"
                      data-testid={`lock-auditor-${a.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setLockFormAuditorIds(prev => v
                            ? [...prev, a.id]
                            : prev.filter(x => x !== a.id));
                        }}
                      />
                      <span>{a.first_name} {a.last_name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                All auditors are selected by default — untick any you want to leave out of the lock. Submitting with every auditor ticked is equivalent to "lock all auditors in the date range".
              </p>
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={lockFormReason}
                onChange={(e) => setLockFormReason(e.target.value)}
                placeholder="e.g. Month-end published — change-control"
                data-testid="lock-reason"
              />
            </div>

            {activeLocks.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <Label className="text-xs uppercase text-muted-foreground">Active locks</Label>
                <div className="mt-1 space-y-1 max-h-40 overflow-y-auto" data-testid="active-locks-list">
                  {activeLocks.map(l => (
                    <div
                      key={l.id}
                      className="flex items-center justify-between text-xs bg-muted/40 px-2 py-1 rounded"
                      data-testid={`active-lock-${l.id}`}
                    >
                      <div>
                        <span className="font-medium">#{l.id}</span>
                        {' '}{l.start_date} → {l.end_date}{' '}
                        <span className="text-muted-foreground">
                          ({(l.auditor_ids || []).length === 0 ? 'all auditors' : `${l.auditor_ids.length} auditor(s)`})
                        </span>
                        <span className="block text-[10px] text-muted-foreground">
                          locked by {l.locked_by_name || '?'} · {l.reason || 'no reason'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => unlockOne(l.id)}
                        data-testid={`unlock-${l.id}-btn`}
                      >
                        Unlock
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLockDialogOpen(false)} data-testid="lock-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitLock}
              disabled={!lockFormStart || !lockFormEnd || lockFormAuditorIds.length === 0 || lockSaving}
              data-testid="lock-confirm-btn"
            >
              {lockSaving ? 'Locking…' : 'Lock Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Combined Approve & Publish dialog ── */}
      <BatchPublishReviewDialog
        open={apDialogOpen}
        onOpenChange={setApDialogOpen}
        onActionComplete={() => {
          loadDraftBatches();
          loadPendingBatches?.();
          loadPendingJobCount();
        }}
        data-testid="approve-publish-dialog-root"
      />
    </div>
  );
}
